import React, { useState, useRef, useEffect } from "react";
import TextareaAutosize from "react-textarea-autosize";

// --- Hook: lock body scroll when a modal is open ---
function useBodyScrollLock(lock) {
  React.useEffect(() => {
    if (!lock) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, [lock]);
}

// --- KQL search normalizer (ensures quotes if missing) ---
function ensureKqlQuoted(term) {
  if (typeof term !== "string") return term;
  const trimmed = term.trim();
  // Keep empty quotes placeholder (""), or if already "text"
  const alreadyQuoted =
    trimmed.length >= 2 && trimmed.startsWith('"') && trimmed.endsWith('"');
  if (alreadyQuoted) return trimmed;
  // If user typed nothing or just quotes, return as-is
  if (trimmed === "" || trimmed === '""') return trimmed;
  // Otherwise wrap in quotes
  return `"${trimmed}"`;
}

// --- Helpers for datetime-local <-> ISO Z ---
// datetime-local returns local time without timezone; we convert it to an ISO string with 'Z'.
function datetimeLocalToIsoZ(value) {
  if (!value) return "";
  // Accepts: YYYY-MM-DDTHH:mm or YYYY-MM-DDTHH:mm:ss
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString();
}

function isoZToDatetimeLocal(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const hh = pad(d.getHours());
  const mi = pad(d.getMinutes());
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}

// --- Helpers: KQL datetime (YYYY-MM-DD HH:mm:ss) ---
// Defender Advanced Hunting UI often shows timestamps like: "2 Feb 2026 16:30:02"
function pad2(n) {
  return String(n).padStart(2, "0");
}
function dateToKqlDatetime(d) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return "";
  const yyyy = d.getFullYear();
  const mm = pad2(d.getMonth() + 1);
  const dd = pad2(d.getDate());
  const hh = pad2(d.getHours());
  const mi = pad2(d.getMinutes());
  const ss = pad2(d.getSeconds());
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss}`;
}
function datetimeLocalToKqlDatetime(value) {
  if (!value) return "";
  const d = new Date(value); // treated as local time
  return dateToKqlDatetime(d);
}
function kqlDatetimeToDatetimeLocal(kql) {
  const raw = String(kql || "").trim();
  const m = raw.match(
    /^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2})(?::(\d{2}))?$/
  );
  if (!m) return "";
  const yyyy = parseInt(m[1], 10);
  const mm = parseInt(m[2], 10);
  const dd = parseInt(m[3], 10);
  const hh = parseInt(m[4], 10);
  const mi = parseInt(m[5], 10);
  const ss = m[6] ? parseInt(m[6], 10) : 0;
  const d = new Date(yyyy, mm - 1, dd, hh, mi, ss, 0);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${yyyy}-${pad(mm)}-${pad(dd)}T${pad(hh)}:${pad(mi)}`;
}
function isoZToKqlDatetime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return dateToKqlDatetime(d);
}
function parseDefenderDisplayedTimestampToKqlDatetime(input) {
  const raw = String(input || "").trim();
  if (!raw) return "";
  // Example: 2 Feb 2026 16:30:02 (Defender Advanced Hunting UI)
  const m = raw.match(
    /^(\d{1,2})\s+([A-Za-z]{3})\s+(\d{4})\s+(\d{2}):(\d{2}):(\d{2})$/
  );
  if (!m) return "";
  const dd = parseInt(m[1], 10);
  const mon = m[2].toLowerCase();
  const yyyy = parseInt(m[3], 10);
  const hh = parseInt(m[4], 10);
  const mi = parseInt(m[5], 10);
  const ss = parseInt(m[6], 10);

  const months = {
    jan: 0,
    feb: 1,
    mar: 2,
    apr: 3,
    may: 4,
    jun: 5,
    jul: 6,
    aug: 7,
    sep: 8,
    oct: 9,
    nov: 10,
    dec: 11,
  };
  if (!(mon in months)) return "";
  const d = new Date(yyyy, months[mon], dd, hh, mi, ss, 0); // local time
  if (Number.isNaN(d.getTime())) return "";
  return dateToKqlDatetime(d);
}

// Helper: try to parse common Sentinel/Defender timestamps into ISO Z.
// Examples it should accept:
// - 2026-01-09T07:04:00.000Z
// - 09/01/2026, 08:04:00.000
// - 09.01.2026 08:04
// - 09-01-2026 08:04:00
// - 2026-01-09 08:04:00
function parseLogTimestampToIsoZ(input) {
  const raw = String(input || "").trim();
  if (!raw) return "";

  // 1) Native parser first (handles ISO, RFC formats, etc.)
  const native = new Date(raw);
  if (!Number.isNaN(native.getTime())) return native.toISOString();

  // 2) dd/mm/yyyy, hh:mm:ss(.ms) with optional comma
  //    09/01/2026, 08:04:00.000  OR  09/01/2026 08:04
  let m = raw.match(
    /^(\d{1,2})[\/.\-](\d{1,2})[\/.\-](\d{4})(?:\s*,\s*|\s+)(\d{1,2}):(\d{2})(?::(\d{2}))?(?:\.(\d{1,6}))?$/
  );
  if (m) {
    const dd = parseInt(m[1], 10);
    const mm = parseInt(m[2], 10);
    const yyyy = parseInt(m[3], 10);
    const HH = parseInt(m[4], 10);
    const MI = parseInt(m[5], 10);
    const SS = m[6] ? parseInt(m[6], 10) : 0;
    let MS = 0;
    if (m[7]) {
      // normalize to milliseconds (max 3 digits)
      const frac = String(m[7]).slice(0, 3).padEnd(3, "0");
      MS = parseInt(frac, 10);
    }
    // Treat pasted timestamps as LOCAL time (same behavior as datetime-local)
    const d = new Date(yyyy, mm - 1, dd, HH, MI, SS, MS);
    if (!Number.isNaN(d.getTime())) return d.toISOString();
  }

  // 3) yyyy-mm-dd hh:mm:ss(.ms)
  m = raw.match(
    /^(\d{4})-(\d{1,2})-(\d{1,2})(?:\s+|T)(\d{1,2}):(\d{2})(?::(\d{2}))?(?:\.(\d{1,6}))?$/
  );
  if (m) {
    const yyyy = parseInt(m[1], 10);
    const mm = parseInt(m[2], 10);
    const dd = parseInt(m[3], 10);
    const HH = parseInt(m[4], 10);
    const MI = parseInt(m[5], 10);
    const SS = m[6] ? parseInt(m[6], 10) : 0;
    let MS = 0;
    if (m[7]) {
      const frac = String(m[7]).slice(0, 3).padEnd(3, "0");
      MS = parseInt(frac, 10);
    }
    const d = new Date(yyyy, mm - 1, dd, HH, MI, SS, MS);
    if (!Number.isNaN(d.getTime())) return d.toISOString();
  }

  return "";
}

// --- Inline styles for flash effect
const GutterStyles = () => (
  <style>
    {`
        .gutter-grid { display: grid; grid-template-columns: auto 1fr; }
        .gutter-col { background:#1f2937; color:#9ca3af; padding:8px 6px 8px 8px; }
        .code-col { background:#1f2937; color:#e5e7eb; }
        .ln { width:24px; text-align:right; font-size:12px; user-select:none; }
        .gutter-row { display:flex; align-items:center; gap:6px; min-height:1.4em; }
        .plus-btn { border:none; background:transparent; color:#10b981; cursor:pointer; line-height:1; font-size:14px; padding:0; }
        .trash-btn { border:none; background:transparent; color:#ef4444; cursor:pointer; line-height:1; font-size:14px; padding:0 4px; }
        .flash-green { animation: flashGreen 1s ease-in-out; }
        @keyframes flashGreen {
          0% { background: rgba(16,185,129,0.25); }
          100% { background: transparent; }
        }
        .inline-input {
          width: 100%;
          background: #111827;
          color: #d1d5db;
          font-size: 0.875rem;
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
          border: 1px solid #374151;
          border-radius: 4px;
          padding: 2px 6px;
        }
        .line-text { white-space: pre; font-size: 0.875rem; }
      `}
  </style>
);

// --- CodeWithGutterEditor: line numbers + "+" per line; inline insert/edit/delete of `| search ""`
function CodeWithGutterEditor({ baseText, queryKey, onChange, searchValue }) {
  const [lines, setLines] = React.useState(() =>
    String(baseText ?? "").split("\n")
  );
  const [editableIdx, setEditableIdx] = React.useState(new Set()); // indices that are editable (inserted)
  const [flashIdx, setFlashIdx] = React.useState(null);
  const [editingIdx, setEditingIdx] = React.useState(new Set());

  const ensureQuoted = (val) => {
    try {
      if (typeof val !== "string") return '""';
      const t = val.trim();
      if (!t) return '""';
      if (t.startsWith('"') && t.endsWith('"')) return t;
      return '"' + t + '"';
    } catch (e) {
      return '""';
    }
  };

  // When baseText changes (e.g., different table), reset
  React.useEffect(() => {
    const arr = String(baseText ?? "").split("\n");
    setLines(arr);
    setEditableIdx(new Set());
    setFlashIdx(null);
    onChange && onChange(queryKey, arr.join("\n"));
  }, [baseText, queryKey]);

  const insertAfter = (idx) => {
    const newLines = [...lines];
    const insertAt = idx + 1;
    const sv = ensureQuoted(searchValue);
    newLines.splice(insertAt, 0, `| search ${sv}`);
    const newEditable = new Set(editableIdx);
    newEditable.add(insertAt);
    const newEditing = new Set(editingIdx);
    // start in view mode; user can click ✏️ to edit
    setLines(newLines);
    setEditableIdx(newEditable);
    setEditingIdx(newEditing);
    setFlashIdx(insertAt);
    onChange && onChange(queryKey, newLines.join("\n"));
    // clear flash after a moment
    setTimeout(() => setFlashIdx(null), 1200);
  };

  const updateLine = (idx, value) => {
    const newLines = [...lines];
    newLines[idx] = value;
    setLines(newLines);
    onChange && onChange(queryKey, newLines.join("\n"));
  };

  const removeLine = (idx) => {
    const newLines = [...lines];
    newLines.splice(idx, 1);
    const newEditable = new Set(editableIdx);
    newEditable.delete(idx);
    // shift editable markers > idx down by 1
    const shifted = new Set();
    newEditable.forEach((i) => shifted.add(i > idx ? i - 1 : i));
    setEditableIdx(shifted);
    setLines(newLines);
    onChange && onChange(queryKey, newLines.join("\n"));
  };

  return (
    <div
      className="relative w-full overflow-x-auto"
      style={{
        fontFamily:
          "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
      }}
    >
      <GutterStyles />
      <div className="gutter-grid">
        <div className="gutter-col">
          {lines.map((_, idx) => (
            <div key={idx} className="gutter-row">
              <span className="ln">{idx + 1}</span>
              <button
                type="button"
                className="plus-btn"
                title='Add | search "" below this line'
                onClick={(e) => {
                  e.stopPropagation();
                  insertAfter(idx);
                }}
              >
                ＋
              </button>
            </div>
          ))}
        </div>
        <div className="code-col p-2">
          {lines.map((ln, idx) => {
            const isEditable = editableIdx.has(idx);
            return (
              <div key={idx} className={flashIdx === idx ? "flash-green" : ""}>
                {isEditable ? (
                  editingIdx.has(idx) ? (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        className="trash-btn"
                        title="Remove this inserted line"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeLine(idx);
                        }}
                      >
                        🗑️
                      </button>
                      <input
                        className="inline-input"
                        value={ln}
                        onChange={(e) => updateLine(idx, e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            const s = new Set(editingIdx);
                            s.delete(idx);
                            setEditingIdx(s);
                          }
                        }}
                      />
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        className="trash-btn"
                        title="Remove this inserted line"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeLine(idx);
                        }}
                      >
                        🗑️
                      </button>
                      <div className="line-text">{ln || " "}</div>
                      <button
                        type="button"
                        className="plus-btn"
                        title="Edit this line"
                        onClick={(e) => {
                          e.stopPropagation();
                          const s = new Set(editingIdx);
                          s.add(idx);
                          setEditingIdx(s);
                        }}
                      >
                        ✏️
                      </button>
                    </div>
                  )
                ) : (
                  <div className="line-text">{ln || " "}</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// Global scrollbar styles for custom dark scrollbars using WebKit pseudo‑elements.
const GlobalScrollbarStyles = () => (
  <style>
    {`
      /* Customize the overall scrollbar size */
      ::-webkit-scrollbar {
        width: 10px;
        height: 10px;
      }
      /* Dark background for the track */
      ::-webkit-scrollbar-track {
        background: #333;
      }
      /* Dark gradient for the thumb */
      ::-webkit-scrollbar-thumb {
        background: linear-gradient(#555, #777, #555);
        border-radius: 20px;
      }
      /* Uncomment below if you want to hide the horizontal scrollbar */
      /* ::-webkit-scrollbar:horizontal {
        display: none;
      } */
      `}
  </style>
);

// --- Responsive layout: keep center content locked and prevent overlap on small screens ---
const ResponsiveShellStyles = () => (
  <style>
    {`
      :root { --qb-left: 320px; --qb-right: 320px; --qb-gap: 16px; }

      /* Shell grid prevents overlap: sidebars are columns, main is the center column */
      .qb-shell {
        display: grid;
        grid-template-columns: var(--qb-left) minmax(0, 1fr) var(--qb-right);
        gap: var(--qb-gap);
        align-items: start;
        padding: 16px;
      }

      /* Make the center column behave nicely */
      .qb-main { min-width: 0; overflow-x: hidden; }

      /* Sidebars: visible immediately, no overlay; sticky on desktop */
      .qb-aside-left, .qb-aside-right {
        min-width: 0;
        max-height: calc(100vh - 32px);
        overflow: auto;
        position: sticky;
        top: 16px;
        border-radius: 16px;
      }

      /* Mobile: still visible immediately, but stacked (no horizontal squeeze/overlap) */
      @media (max-width: 1100px) {
        .qb-shell { grid-template-columns: 1fr; }
        .qb-left { grid-column: 1; grid-row: 1; position: relative; top: auto; max-height: none; }
        .qb-main { grid-column: 1; grid-row: 2; }
        .qb-right{ grid-column: 1; grid-row: 3; position: relative; top: auto; max-height: none; }
        .qb-aside-left, .qb-aside-right { position: relative; top: auto; max-height: none; }
      }

      /* Hide old mobile drawer bar / overlay controls (we're using stacked layout instead) */
      .qb-mobile-bar { display: none !important; }
    `}
  </style>
);

// Helper function – formats a date to the Sentinel format (dd/mm/yyyy, hh:mm:ss.mmm)
function formatSentinelDate(date) {
  const day = ("0" + date.getDate()).slice(-2);
  const month = ("0" + (date.getMonth() + 1)).slice(-2);
  const year = date.getFullYear();
  const hours = ("0" + date.getHours()).slice(-2);
  const minutes = ("0" + date.getMinutes()).slice(-2);
  const seconds = ("0" + date.getSeconds()).slice(-2);
  const ms = ("00" + date.getMilliseconds()).slice(-3);
  return `${day}/${month}/${year}, ${hours}:${minutes}:${seconds}.${ms}`;
}

// Helper: convert ISO string to 'YYYY-MM-DDTHH:mm' for <input type="datetime-local">
function isoToLocalInput(iso) {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    const yyyy = d.getFullYear();
    const mm = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    const hh = pad(d.getHours());
    const mi = pad(d.getMinutes());
    return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
  } catch (e) {
    return "";
  }
}

// Helper: convert 'YYYY-MM-DDTHH:mm' (local) to ISO string
function localInputToIso(localStr) {
  try {
    if (!localStr) return new Date().toISOString();
    // Treat as local time; construct Date and convert to ISO (UTC)
    const d = new Date(localStr);
    if (isNaN(d.getTime())) return new Date().toISOString();
    return d.toISOString();
  } catch (e) {
    return new Date().toISOString();
  }
}
// Initial tables list (could be empty or prepopulated)
const initialTables = [];

// Predefined blue tile queries shown on the dashboard
const defaultBlueTiles = [
  {
    id: "tile-all",
    label: "Check all tables in last 12h",
    queryTemplate: `search "{searchValue}"\n| where TimeGenerated > ago(12h)\n| summarize count() by $table`,
    isPredefined: true,
  },
  {
    id: "tile-mfa",
    label: "Check MFA last 24h",
    queryTemplate: `SigninLogs\n| search ${ensureKqlQuoted(
      "{searchValue}"
    )}\n| where TimeGenerated > ago(24h)\n| extend displayName_ = tostring(DeviceDetail.displayName), browser_ = tostring(DeviceDetail.browser), operatingSystem_ = tostring(DeviceDetail.operatingSystem), UserPrincipalName1 = tostring(UserPrincipalName), MFA_Step_Requirement = tostring(parse_json(AuthenticationDetails)[1].authenticationStepRequirement), MFA_Succeeded = tostring(parse_json(AuthenticationDetails)[1].succeeded), authMethod_ = tostring(MfaDetail.authMethod), additionalDetails_ = tostring(Status.additionalDetails), vt_table = strcat("https://www.virustotal.com/gui/ip-address/", IPAddress), Country = tostring(geo_info_from_ip_address(IPAddress).country)`,
    isPredefined: true,
  },
  { id: "tile-plus", label: "+", isPlus: true },
];

const defaultButtonPositions = {
  settingsButton: { top: 10, left: 10 },
};

export default function QueryBuilder() {
  // --- States for Right Sidebar Tool Modals ---
  // --- Mobile drawers (sidebars on small screens) ---
  const [leftDrawerOpen, setLeftDrawerOpen] = useState(false);
  const [rightDrawerOpen, setRightDrawerOpen] = useState(false);
  useBodyScrollLock(leftDrawerOpen || rightDrawerOpen);

  const [showAddCharsModal, setShowAddCharsModal] = useState(false);
  const [showDecodeUrlModal, setShowDecodeUrlModal] = useState(false);
  const [showDecodeBase64Modal, setShowDecodeBase64Modal] = useState(false);
  const [showFindReplaceModal, setShowFindReplaceModal] = useState(false);
  const [findReplaceInput, setFindReplaceInput] = useState("");
  const [findString, setFindString] = useState("");
  const [replaceString, setReplaceString] = useState("");
  // Paste KQL + add Event Time Filter
  const [showPasteEventTimeModal, setShowPasteEventTimeModal] = useState(false);
  const [pastedKqlInput, setPastedKqlInput] = useState("");
  // ISO time used in the generated KQL
  const [pasteEventTime, setPasteEventTime] = useState(() =>
    dateToKqlDatetime(new Date())
  );
  // GUI time picker value (datetime-local)
  const [pasteEventTimeLocal, setPasteEventTimeLocal] = useState(() =>
    kqlDatetimeToDatetimeLocal(dateToKqlDatetime(new Date()))
  );
  // Optional: user can paste a timestamp from Sentinel/Defender logs
  const [pasteEventTimeFromLogs, setPasteEventTimeFromLogs] = useState("");
  // Target platform for the time-filter snippet (Defender AH vs Sentinel)
  const [pastePlatform, setPastePlatform] = useState("defender"); // "defender" | "sentinel"
  // Sentinel prefers ISO 8601 with Z (e.g., 2026-02-02T15:16:00.000Z)
  const [pasteEventTimeIsoZ, setPasteEventTimeIsoZ] = useState(() =>
    new Date().toISOString()
  );

  const [pasteOffsetBefore, setPasteOffsetBefore] = useState(2);
  const [pasteOffsetAfter, setPasteOffsetAfter] = useState(2);
  const [showNonClickableUrlModal, setShowNonClickableUrlModal] =
    useState(false);
  const [nonClickableUrlInput, setNonClickableUrlInput] = useState("");
  const [ipToCheck, setIpToCheck] = useState("");
  const [showEmailHeaderModal, setShowEmailHeaderModal] = useState(false);
  const [emailHeaderInput, setEmailHeaderInput] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailSender, setEmailSender] = useState("");
  const [emailRecipient, setEmailRecipient] = useState("");
  const [emailCreationTime, setEmailCreationTime] = useState("");
  const [extractedIPs, setExtractedIPs] = useState([]);

  const extractIPsFromHeader = (header) => {
    // Extract header fields
    const subjectMatch = header.match(/^Subject:\s*(.*)$/m);
    setEmailSubject(subjectMatch ? subjectMatch[1] : "");
    const fromMatch = header.match(/^From:\s*(.*)$/m);
    setEmailSender(fromMatch ? fromMatch[1] : "");
    const toMatch = header.match(/^To:\s*(.*)$/m);
    setEmailRecipient(toMatch ? toMatch[1] : "");
    const dateMatch = header.match(/^Date:\s*(.*)$/m);
    setEmailCreationTime(dateMatch ? dateMatch[1] : "");
    const ips = Array.from(
      new Set(header.match(/\b(?:\d{1,3}\.){3}\d{1,3}\b/g) || [])
    );
    const results = ips.map((ip) => {
      const isPrivate = /^10\.|^192\.168\.|^172\.(1[6-9]|2\d|3[01])\./.test(ip);
      return {
        ip,
        type: isPrivate ? "Private" : "External",
        url: !isPrivate
          ? `https://www.virustotal.com/gui/ip-address/${ip}`
          : "",
      };
    });
    setExtractedIPs(results);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setShowAddCharsModal(false);
        setShowDecodeUrlModal(false);
        setShowDecodeBase64Modal(false);
        setShowFindReplaceModal(false);
        setShowPasteEventTimeModal(false);
        setShowNonClickableUrlModal(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Keep GUI time picker in sync when opening the Paste KQL modal
  useEffect(() => {
    if (!showPasteEventTimeModal) return;
    const kql = pasteEventTime || dateToKqlDatetime(new Date());
    setPasteEventTime(kql);
    setPasteEventTimeLocal(kqlDatetimeToDatetimeLocal(kql));
  }, [showPasteEventTimeModal]);

  // For Add Characters tool
  const [addCharsBegin, setAddCharsBegin] = useState("");
  const [addCharsEnd, setAddCharsEnd] = useState("");
  const [addCharsInput, setAddCharsInput] = useState("");
  // For Decode URL
  const [decodeUrlInput, setDecodeUrlInput] = useState("");
  // For Decode Base64
  const [decodeBase64Input, setDecodeBase64Input] = useState("");

  // --- New Tool Functions ---
  const computeAddCharsOutput = () =>
    addCharsInput
      .split("\n")
      .map((line) => addCharsBegin + line + addCharsEnd)
      .join("\n");

  const getDecodedUrl = () => {
    try {
      return decodeURIComponent(decodeUrlInput);
    } catch (e) {
      return "Invalid URL encoding";
    }
  };

  const getDecodedBase64 = () => {
    try {
      return window.atob(decodeBase64Input);
    } catch (e) {
      return "Invalid Base64 string";
    }
  };

  const getFindReplaceOutput = () => {
    if (!findString) return findReplaceInput;
    try {
      const regex = new RegExp(findString, "g");
      return findReplaceInput.replace(regex, replaceString);
    } catch (e) {
      return "Invalid regular expression in Find field.";
    }
  };

  const convertNonClickableUrl = () => {
    // Replace all URLs in the input with non-clickable versions
    const urlRegex = /https?:\/\/[^\s]+/g;
    return nonClickableUrlInput.replace(urlRegex, (url) => {
      // Replace protocol
      let modified = url.replace(/^https?:\/\//, (match) =>
        match === "https://" ? "hXXps://" : "hXXp://"
      );
      // Replace all dots
      modified = modified.replace(/\./g, "[.]");
      return modified;
    });
  };

  // Build KQL by appending the standard "Set Event Time Filter" block to a pasted query
  const getPastedKqlWithEventTimeFilter = () => {
    const raw = String(pastedKqlInput || "").trim();
    if (!raw) return "";

    const toMin = (v) => {
      const n = parseFloat(String(v).replace(/[^0-9.-]/g, ""));
      return isNaN(n) ? 0 : n;
    };
    const ob = toMin(pasteOffsetBefore);
    const oa = toMin(pasteOffsetAfter);

    const hasLetTime = /(^|\n)\s*let\s+Time\s*=\s*datetime\s*\(/i.test(raw);
    const hasEventTimeExtend =
      /\|\s*extend\s+EventTime\s*=\s*coalesce\s*\(/i.test(raw);
    const hasEventTimeWhere = /\|\s*where\s+EventTime\s+between\s*\(/i.test(
      raw
    );

    const extendClause = `| extend EventTime = coalesce(column_ifexists("Timestamp", datetime(null)), column_ifexists("TimeGenerated", datetime(null)))`;

    // Defender Advanced Hunting (AH) uses: Time - Xm .. Time + Ym
    // Sentinel (Log Analytics) often uses datetime_add('minute', X, Time)
    // IMPORTANT: keep the exact same time value in the UI and in the generated query
    // (no 1-hour drift due to ISO(Z) vs local formatting).
    const isoZ =
      pasteEventTimeIsoZ || datetimeLocalToIsoZ(pasteEventTimeLocal) || "";
    const letTimeValue =
      pasteEventTime ||
      (isoZ ? isoZToKqlDatetime(isoZ) : "") ||
      datetimeLocalToKqlDatetime(pasteEventTimeLocal) ||
      "";
    const letClause = `let Time = datetime(${letTimeValue});`;

    const whereClause =
      pastePlatform === "sentinel"
        ? `| where EventTime between (datetime_add('minute', -${ob}, Time) .. datetime_add('minute', ${oa}, Time))`
        : `| where EventTime between (Time - ${ob}m .. Time + ${oa}m)`;

    const parts = [];
    if (!hasLetTime) parts.push(letClause);
    parts.push(raw);
    if (!hasEventTimeExtend) parts.push(extendClause);
    if (!hasEventTimeWhere) parts.push(whereClause);

    return parts.join("\n");
  };

  const [modalOpen, setModalOpen] = useState(false);
  const [currentModalKey, setCurrentModalKey] = useState(null);
  const [modalQueryText, setModalQueryText] = useState("");
  const [selectedTables, setSelectedTables] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [tables, setTables] = useState(() => {
    const saved = localStorage.getItem("kqltool_tables");
    return saved ? JSON.parse(saved) : initialTables;
  });
  const [customQueries, setCustomQueries] = useState({});
  const [newTableName, setNewTableName] = useState("");
  const [newTableQuery, setNewTableQuery] = useState("");

  // --- Saved Queries (My Queries) ---
  const [savedQueries, setSavedQueries] = useState(() => {
    try {
      const raw = localStorage.getItem("kqltool_savedQueries");
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [savedQueriesSearch, setSavedQueriesSearch] = useState("");
  const [showSavedQueriesSearch, setShowSavedQueriesSearch] = useState(false);
  const [showSaveQueryModal, setShowSaveQueryModal] = useState(false);
  const [editingSavedQueryId, setEditingSavedQueryId] = useState(null);
  const [saveQueryForm, setSaveQueryForm] = useState({
    title: "",
    description: "",
    tags: [],
    query: "",
    pinned: false,
  });
  const [saveQueryCurrentTag, setSaveQueryCurrentTag] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  // Platform selector for "Define your tables from your environment" modal
  const [envPlatform, setEnvPlatform] = useState("sentinel"); // default: Sentinel
  const [copiedResult, setCopiedResult] = useState("");
  const [showSavedCopyToast, setShowSavedCopyToast] = useState(false);
const [favoriteQueries, setFavoriteQueries] = useState({});
  const [selectedFavorite, setSelectedFavorite] = useState({});
  const [customFavoriteNames, setCustomFavoriteNames] = useState({});
  const [blueTiles, setBlueTiles] = useState(() => defaultBlueTiles);
  // Holds edited query text per card (keyed by tableName::index)
  const [editedQueries, setEditedQueries] = useState({});
  // draft edits (NOT persisted) - only committed when user clicks "Save Custom Query"
  const [draftQueries, setDraftQueries] = useState({});

  // "Define your tables" helper queries (Sentinel vs Defender)
  const DEFINE_TABLES_QUERY_DEFENDER = `search *
| where Timestamp >= ago(30d)
| distinct $table
| sort by $table asc
| summarize Tables = strcat_array(make_list($table), "\\n")`;

  const DEFINE_TABLES_QUERY_SENTINEL = `search *
| where TimeGenerated >= ago(30d)
| distinct $table
| sort by $table asc
| summarize Tables = strcat_array(make_list($table), "\\n")`;

  const defineTablesKql =
    envPlatform === "defender"
      ? DEFINE_TABLES_QUERY_DEFENDER
      : DEFINE_TABLES_QUERY_SENTINEL;

  // Map of 'queryKey' -> boolean (true when inline editor is active)
  const [editingMap, setEditingMap] = useState({});
  // Helper to open/close editor per query key (draft is not persisted)
  const toggleEditFor = (key, initialText = "") => {
    setEditingMap((prev) => {
      const next = !prev[key];
      return { ...prev, [key]: next };
    });
    setDraftQueries((prev) => {
      // when opening, initialize draft from saved version (or current query text)
      if (prev[key] === undefined)
        return { ...prev, [key]: editedQueries[key] ?? initialText };
      return prev;
    });
    // when closing, discard draft (user must click "Save Custom Query" to persist)
    setTimeout(() => {
      setEditingMap((em) => {
        if (em[key]) return em; // still open
        setDraftQueries((dq) => {
          if (dq[key] === undefined) return dq;
          const { [key]: _, ...rest } = dq;
          return rest;
        });
        return em;
      });
    }, 0);
  };
  // Helper to set edited text for a key
  const setEditedText = (key, txt) =>
    setDraftQueries((prev) => ({ ...prev, [key]: txt }));
  const getQueryKey = (tableName, idx) => `${tableName}::${idx}`;
  const handleEditedChange = (key, text) => {
    setDraftQueries((prev) => ({ ...prev, [key]: text }));
  };

  // Persist custom queries in browser + add to favorites
  // Edit Query feature: Save Custom Query (persist edit + add to Favorites without prompt)
  const saveCustomQuery = (tableName, index, key, currentText) => {
    try {
      // 1) persist edited text
      setEditedQueries((prev) => ({ ...prev, [key]: currentText }));

      // commit done -> close editor and clear draft
      setEditingMap((prev) => ({ ...prev, [key]: false }));
      setDraftQueries((prev) => {
        if (prev[key] === undefined) return prev;
        const { [key]: _, ...rest } = prev;
        return rest;
      });
      // 2) ensure it's in Favorites
      setFavoriteQueries((prev) => {
        const arr = prev[tableName] || [];
        if (arr.includes(index)) return prev;
        return { ...prev, [tableName]: [...arr, index] };
      });

      // 3) ask for Name / Tags (same modal as Favorites)
      setPendingCustomSave({ tableName, index, key, text: currentText });
      setTagModalData({
        visible: true,
        tableName,
        queryIndex: index,
        customName: "",
        tags: [],
      });
      setCurrentTag("");
    } catch (e) {
      console.error("Save Custom Query failed:", e);
    }
  };

  const [showAddTileModal, setShowAddTileModal] = useState(false);
  // --- Search Around Event states ---
  const [showAroundPopup, setShowAroundPopup] = useState(false);
  const aroundModalRef = useRef(null);
  useBodyScrollLock(showAroundPopup);
  const [aroundConfig, setAroundConfig] = useState({
    meanEvent: "",
    contextFilter: "",
    repeatMeanEvent: "",
    eventTime: new Date().toISOString(),
    offsetBefore: "2",
    offsetAfter: "2",
  });

  useEffect(() => {
    if (!showAroundPopup || !aroundModalRef.current) return;
    // Focus the first focusable element inside the modal
    const focusableSelectors = [
      "button",
      "a[href]",
      "input",
      "select",
      "textarea",
      '[tabindex]:not([tabindex="-1"])',
    ];
    const focusables = aroundModalRef.current.querySelectorAll(
      focusableSelectors.join(",")
    );
    if (focusables.length) {
      focusables[0].focus();
    }
    const handleKeyDown = (e) => {
      if (e.key !== "Tab") return;
      const nodes = Array.from(
        aroundModalRef.current.querySelectorAll(focusableSelectors.join(","))
      );
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [showAroundPopup]);
  const [selectedUnionTables, setSelectedUnionTables] = useState([]);
  const [aroundQueryText, setAroundQueryText] = useState("");
  const [showAroundQuery, setShowAroundQuery] = useState(false);
  const aroundQueryRef = useRef(null);
  const dateGuiRef = useRef(null);
  const [showQueryModal, setShowQueryModal] = useState(false);
  const [modalQuery, setModalQuery] = useState("");
  const [customTableName, setCustomTableName] = useState("");
  const [showDateGui, setShowDateGui] = useState(false);
  const [dateGui, setDateGui] = useState("");

  const modalTextRef = useRef(null);

  useEffect(() => {
    if (showQueryModal && modalTextRef.current) {
      modalTextRef.current.select();
    }
  }, [showQueryModal]);

  useEffect(() => {
    if (showAroundQuery && aroundQueryRef.current) {
      aroundQueryRef.current.select();
    }
  }, [showAroundQuery]);

  useEffect(() => {
    if (
      showDateGui &&
      dateGuiRef.current &&
      typeof dateGuiRef.current.showPicker === "function"
    ) {
      // Delay slightly to ensure element is in DOM and visible for the picker
      setTimeout(() => {
        try {
          dateGuiRef.current.showPicker();
        } catch (e) {}
      }, 0);
    }
  }, [showDateGui]);

  const [newTileName, setNewTileName] = useState("");
  const [newTileQuery, setNewTileQuery] = useState("");
  const [editingTile, setEditingTile] = useState(null);
  const [favoriteDropdown, setFavoriteDropdown] = useState({});
  const [dropdownFilter, setDropdownFilter] = useState("");
  const [favoritesSearch, setFavoritesSearch] = useState("");
  const [tableNavSearch, setTableNavSearch] = useState("");
  const [timeInterval, setTimeInterval] = useState("24h");
  const [applyTimeFilter, setApplyTimeFilter] = useState(false);
  // Event filter settings state: maps a table and its query index to filter configuration
  const [eventFilterSettings, setEventFilterSettings] = useState({});
  const [eventFilterModal, setEventFilterModal] = useState({
    visible: false,
    tableName: "",
    queryIndex: null,
    eventTime: new Date().toISOString(),
    offsetBefore: "2",
    offsetAfter: "2",
  });
  const [tagModalData, setTagModalData] = useState({
    visible: false,
    tableName: "",
    queryIndex: null,
    customName: "",
    tags: [],
  });
  // When saving a custom query from Generated Query, we ask for Name / Tags
  const [pendingCustomSave, setPendingCustomSave] = useState(null);

  const [currentTag, setCurrentTag] = useState("");
  const [showSettingsPanel, setShowSettingsPanel] = useState(false);
  const [showGuide, setShowGuide] = useState(() => {
    const saved = localStorage.getItem("kqltool_showGuide");
    return saved ? JSON.parse(saved) : true;
  });
  const [showEnvGuide, setShowEnvGuide] = useState(true);
  const [showInstructions, setShowInstructions] = useState(true);
  const [settingsData, setSettingsData] = useState("");
  const [settingsURL, setSettingsURL] = useState("");
  const [buttonPositions, setButtonPositions] = useState(
    defaultButtonPositions
  );

  const tileModalRef = useRef(null);
  const [tileModalSize, setTileModalSize] = useState({
    width: 800,
    height: 600,
  });
  const [isTileResizing, setIsTileResizing] = useState(false);
  const dragTileItem = useRef(null);
  const dragOverTileItem = useRef(null);
  const dragSavedQueryId = useRef(null);
  const settingsRef = useRef(null);
  const tablesContainerRef = useRef(null);
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  useEffect(() => {
    setTileModalSize({
      width: window.innerWidth - 150,
      height: window.innerHeight - 150,
    });
  }, []);

  useEffect(() => {
    const savedState = localStorage.getItem("kqltool_state");
    if (savedState) {
      const state = JSON.parse(savedState);
      if (state.selectedTables) setSelectedTables(state.selectedTables);
      if (state.customQueries) setCustomQueries(state.customQueries);
      if (state.favoriteQueries) setFavoriteQueries(state.favoriteQueries);
      if (state.customFavoriteNames)
        setCustomFavoriteNames(state.customFavoriteNames);
      if (state.eventFilterSettings)
        setEventFilterSettings(state.eventFilterSettings);
      if (state.timeInterval) setTimeInterval(state.timeInterval);
      if (state.applyTimeFilter !== undefined)
        setApplyTimeFilter(state.applyTimeFilter);
      if (state.blueTiles) setBlueTiles(state.blueTiles);
      if (state.buttonPositions) setButtonPositions(state.buttonPositions);
      if (state.showGuide !== undefined) setShowGuide(state.showGuide);

      if (state.editedQueries) setEditedQueries(state.editedQueries);
    }
  }, []);

  useEffect(() => {
    const settings = {
      selectedTables,
      customQueries,
      favoriteQueries,
      customFavoriteNames,
      eventFilterSettings,
      timeInterval,
      applyTimeFilter,
      showGuide,
      blueTiles,
      buttonPositions,
      editedQueries,
    };
    localStorage.setItem("kqltool_state", JSON.stringify(settings));
  }, [
    selectedTables,
    customQueries,
    favoriteQueries,
    customFavoriteNames,
    eventFilterSettings,
    timeInterval,
    applyTimeFilter,
    showGuide,
    blueTiles,
    buttonPositions,
    editedQueries,
  ]);

  useEffect(() => {
    localStorage.setItem("kqltool_tables", JSON.stringify(tables));
  }, [tables]);

  useEffect(() => {
    try {
      localStorage.setItem(
        "kqltool_savedQueries",
        JSON.stringify(savedQueries)
      );
    } catch (e) {
      console.error("Failed to persist saved queries", e);
    }
  }, [savedQueries]);

  useEffect(() => {
    localStorage.setItem("kqltool_showGuide", JSON.stringify(showGuide));
  }, [showGuide]);

  const defineTablesFromEnv = () => setShowPopup(true);

  // --- Saved Queries helpers ---
  const normalizeTags = (tagsInput) => {
    const rawArr = Array.isArray(tagsInput)
      ? tagsInput
      : String(tagsInput || "")
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean);

    // de-dupe while preserving order (case-insensitive)
    const out = [];
    const seen = new Set();
    for (const t of rawArr) {
      const clean = String(t || "").trim();
      if (!clean) continue;
      const key = clean.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(clean);
      if (out.length >= 3) break; // max 3 tags
    }
    return out;
  };

  const getNextDefaultSavedTitle = () => {
    // Titles like: new(1), new(2) ...
    const re = /^new\((\d+)\)$/i;
    let maxN = 0;
    for (const q of savedQueries || []) {
      const m = re.exec(String(q?.title || "").trim());
      if (m) {
        const n = Number(m[1]);
        if (Number.isFinite(n) && n > maxN) maxN = n;
      }
    }
    return `new(${maxN + 1})`;
  };

  const openSaveQueryModal = (mode = "new", existing = null) => {
    if (mode === "edit" && existing) {
      setEditingSavedQueryId(existing.id);
      setSaveQueryForm({
        title: existing.title || "",
        description: existing.description || "",
        tags: normalizeTags(existing.tags || []),
        query: existing.query || "",
        pinned: !!existing.pinned,
      });
      setSaveQueryCurrentTag("");
    } else {
      setEditingSavedQueryId(null);
      setSaveQueryForm({
        title: getNextDefaultSavedTitle(),
        description: "",
        tags: [],
        // default to whatever is currently in the main KQL input
        query: (newTableQuery || "").trim(),
        pinned: false,
      });
      setSaveQueryCurrentTag("");
    }
    setShowSaveQueryModal(true);
  };

  const addSaveQueryTag = () => {
    const t = String(saveQueryCurrentTag || "")
      .trim()
      .replace(/^#/, "");
    if (!t) return;
    setSaveQueryForm((p) => {
      const next = normalizeTags([...(p.tags || []), t]);
      return { ...p, tags: next };
    });
    setSaveQueryCurrentTag("");
  };

  const handleSaveQueryTagKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if ((saveQueryForm.tags || []).length >= 3) return;
      addSaveQueryTag();
    }
  };

  const saveOrUpdateSavedQuery = () => {
    const title = String(saveQueryForm.title || "").trim();
    const description = String(saveQueryForm.description || "").trim();
    const query = String(saveQueryForm.query || "").trim();
    const tags = normalizeTags(saveQueryForm.tags);

    if (!title || !query) {
      alert("Title and query are required.");
      return;
    }

    const now = Date.now();
    if (editingSavedQueryId) {
      const updated = {
        id: editingSavedQueryId,
        title,
        description,
        tags,
        query,
        pinned: !!saveQueryForm.pinned,
        updatedAt: now,
      };
      setSavedQueries((prev) =>
        prev.map((q) =>
          q.id === editingSavedQueryId ? { ...q, ...updated } : q
        )
      );
    } else {
      const item = {
        id: `sq-${now}-${Math.random().toString(16).slice(2)}`,
        title,
        description,
        tags,
        query,
        pinned: !!saveQueryForm.pinned,
        createdAt: now,
        updatedAt: now,
      };
      setSavedQueries((prev) => [item, ...prev]);
      // Create a new tile next to existing ones
    }

    setShowSaveQueryModal(false);
  };

  const deleteSavedQuery = (id) => {
    if (!window.confirm("Delete this saved query?")) return;
    setSavedQueries((prev) => prev.filter((q) => q.id !== id));
  };

  // Drag & drop reorder for Saved Queries tiles
  const handleSavedQueryDragStart = (e, id) => {
    dragSavedQueryId.current = id;
    e.dataTransfer.effectAllowed = "move";
    // visual feedback
    setTimeout(() => {
      try {
        e.currentTarget.classList.add("opacity-50");
      } catch {}
    }, 0);
  };

  const handleSavedQueryDragEnter = (e) => {
    e.preventDefault();
    try {
      e.currentTarget.classList.add("ring-2", "ring-indigo-300");
    } catch {}
  };

  const handleSavedQueryDragLeave = (e) => {
    try {
      e.currentTarget.classList.remove("ring-2", "ring-indigo-300");
    } catch {}
  };

  const handleSavedQueryDragOver = (e) => {
    e.preventDefault();
  };

  const handleSavedQueryDragEnd = (e) => {
    try {
      e.currentTarget.classList.remove(
        "opacity-50",
        "ring-2",
        "ring-indigo-300"
      );
    } catch {}
  };

  const handleSavedQueryDrop = (e, targetId) => {
    e.preventDefault();
    e.stopPropagation();

    const draggedId = dragSavedQueryId.current;
    if (!draggedId || draggedId === targetId) return;

    setSavedQueries((prev) => {
      const arr = [...prev];
      const from = arr.findIndex((x) => x.id === draggedId);
      const to = arr.findIndex((x) => x.id === targetId);
      if (from === -1 || to === -1) return prev;

      const [moved] = arr.splice(from, 1);
      arr.splice(to, 0, moved);
      return arr;
    });

    dragSavedQueryId.current = null;

    try {
      e.currentTarget.classList.remove("ring-2", "ring-indigo-300");
    } catch {}
  };
  const loadSavedQueryIntoEditor = (q) => {
    if (!q?.query) return;
    setNewTableQuery(q.query);
    // best-effort scroll to the input area
    setTimeout(() => {
      const el = document.getElementById("add-new-table-query");
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.focus?.();
      }
    }, 0);
  };

  // --- Saved Queries: single-click copy, double-click edit ---
  const sqClickTimerRef = useRef(null);

  const handleSavedQueryTileClick = (q) => {
    if (!q) return;
    // Single-click copies the content (KQL or investigation notes).
    // Double-click cancels this timer and opens edit modal (see handleSavedQueryTileDoubleClick).
    if (sqClickTimerRef.current) clearTimeout(sqClickTimerRef.current);
    sqClickTimerRef.current = setTimeout(async () => {
      const content = String(q.query ?? "");
      if (!content.trim()) return;
      try {
        await navigator.clipboard.writeText(content);
        setCopiedResult(content);
        setShowSavedCopyToast(true);
        setTimeout(() => setShowSavedCopyToast(false), 1200);
      } catch {
        // Fallback: load into editor if clipboard denied
        loadSavedQueryIntoEditor(q);
      }
    }, 220);
  };

  const handleSavedQueryTileDoubleClick = (q) => {
    if (sqClickTimerRef.current) clearTimeout(sqClickTimerRef.current);
    openSaveQueryModal("edit", q);
  };

  useEffect(() => {
    if (showAroundPopup) {
      if (selectedTables.length > 0) {
        setSelectedUnionTables(selectedTables);
      } else {
        setSelectedUnionTables(tables.map((t) => t.name));
      }
    }
  }, [showAroundPopup, selectedTables, tables]);
  // Function to generate the KQL query for Search Around Event
  const generateUnionQuery = () => {
    const {
      meanEvent,
      contextFilter,
      repeatMeanEvent,
      eventTime,
      offsetBefore,
      offsetAfter,
    } = aroundConfig;
    const toMin = (v) => {
      const n = parseFloat(String(v).replace(/[^0-9.-]/g, ""));
      return isNaN(n) ? 0 : n;
    };
    const offB = toMin(offsetBefore);
    const offA = toMin(offsetAfter);
    const tablesList = selectedUnionTables.join(",\n    ");
    return `let Time = datetime(${eventTime});
  union isfuzzy=true
      ${tablesList}
  | extend EventTime = coalesce(column_ifexists("Timestamp", datetime(null)), column_ifexists("TimeGenerated", datetime(null)))
  | where EventTime between (datetime_add('minute', -${offB}, Time) .. datetime_add('minute', ${offA}, Time))
  | extend EventLabel = case(
      * has "${meanEvent}", "🟥 Alert Event",
      (* has "${contextFilter}" and not(* has "${meanEvent}")), "🟦 Context Event",
      ""
  )
  | where EventLabel != ""
  | project-reorder EventLabel, *`;
  };
  const closePopup = () => {
    setShowPopup(false);
    setCopiedResult("");
  };
  const handleAddTables = () => {
    const tablesFromInput = copiedResult
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line);
    const newTables = tablesFromInput.filter(
      (table) => !tables.some((t) => t.name === table)
    );
    setTables([
      ...tables,
      ...newTables.map((name) => ({
        name,
        comment: `// table name_is_${name}`,
      })),
    ]);
    const selectedFromInput = tablesFromInput.filter((table) =>
      tables.some((t) => t.name === table)
    );
    setSelectedTables((prev) => [...prev, ...selectedFromInput]);
    setShowPopup(false);
  };
  const handleCloseModalOnClickOutside = (closeFunc) => (e) => {
    closeFunc();
  };

  useEffect(() => {
    const handleClickOutsideFavoriteDropdown = (e) => {
      if (!e.target.closest(".favorite-dropdown")) {
        setFavoriteDropdown({});
      }
    };
    document.addEventListener("click", handleClickOutsideFavoriteDropdown);
    return () => {
      document.removeEventListener("click", handleClickOutsideFavoriteDropdown);
    };
  }, []);

  const handleTagInputKeyDown = (e) => {
    if (e.key === "Enter" && currentTag.trim()) {
      e.preventDefault();
      if (tagModalData.tags.length < 3) {
        setTagModalData((prev) => ({
          ...prev,
          tags: [...prev.tags, currentTag.trim()],
        }));
        setCurrentTag("");
      }
    }
  };
  const handleEditTag = (index) => {
    const tagToEdit = tagModalData.tags[index];
    setCurrentTag(tagToEdit);
    setTagModalData((prev) => ({
      ...prev,
      tags: prev.tags.filter((_, i) => i !== index),
    }));
  };
  const handleAddTagClick = () => {
    if (currentTag.trim() && tagModalData.tags.length < 3) {
      setTagModalData((prev) => ({
        ...prev,
        tags: [...prev.tags, currentTag.trim()],
      }));
      setCurrentTag("");
    }
  };

  const handleInsertHash = () => {
    if (!currentTag.startsWith("#")) {
      setCurrentTag("#" + currentTag);
    }
  };

  const handleAddTag = () => {
    const { tableName, queryIndex } = tagModalData;

    // Ensure it's in Favorites (in case modal opened from Save Custom Query)
    setFavoriteQueries((prev) => {
      const arr = prev[tableName] || [];
      if (arr.includes(queryIndex)) return prev;
      return { ...prev, [tableName]: [...arr, queryIndex] };
    });

    setCustomFavoriteNames((prev) => ({
      ...prev,
      [tableName]: {
        ...(prev[tableName] || {}),
        [queryIndex]: {
          customName: tagModalData.customName.trim(),
          tags: tagModalData.tags,
        },
      },
    }));

    // If this modal came from "Save Custom Query", close the inline editor now
    // and also save it as a NEW table (same flow as "Add a new table")
    setPendingCustomSave((pending) => {
      if (
        pending &&
        pending.tableName === tableName &&
        pending.index === queryIndex
      ) {
        const k = pending.key || `${tableName}::${queryIndex}`;
        setEditingMap((prev) => ({ ...prev, [k]: false }));

        const proposedName = (tagModalData.customName || "").trim();
        const newName =
          proposedName ||
          `CustomQuery_${pending.tableName}_${new Date()
            .toISOString()
            .replace(/[^0-9]/g, "")
            .slice(0, 14)}`;

        const text = String(pending.text || "").trim();
        if (text) {
          setCustomQueries((prev) => ({ ...prev, [newName]: text }));
          setTables((prev) => {
            if (prev.some((t) => t.name === newName)) return prev;
            return [
              ...prev,
              {
                name: newName,
                comment: `// table name_is_${newName}`,
              },
            ];
          });
          setSelectedTables((prev) =>
            prev.includes(newName) ? prev : [...prev, newName]
          );
        }

        return null;
      }
      return pending;
    });

    setTagModalData({
      visible: false,
      tableName: "",
      queryIndex: null,
      customName: "",
      tags: [],
    });
    setCurrentTag("");
  };

  const toggleFavoriteQuery = (tableName, queryIndex) => {
    setFavoriteQueries((prev) => {
      const currentFavs = prev[tableName] || [];
      if (currentFavs.includes(queryIndex)) {
        setCustomFavoriteNames((prevNames) => {
          const newObj = { ...prevNames };
          if (newObj[tableName]) {
            delete newObj[tableName][queryIndex];
          }
          return newObj;
        });
        return {
          ...prev,
          [tableName]: currentFavs.filter((i) => i !== queryIndex),
        };
      } else {
        setTagModalData({
          visible: true,
          tableName,
          queryIndex,
          customName: "",
          tags: [],
        });
        setCurrentTag("");
        return { ...prev, [tableName]: [...currentFavs, queryIndex] };
      }
    });
  };

  const handleSettingsDataChange = (e) => {
    setSettingsData(e.target.value);
  };

  const handleImportSettings = () => {
    try {
      const imported = JSON.parse(settingsData);
      setTables(imported.tables || initialTables);
      setSelectedTables(imported.selectedTables || []);
      setCustomQueries(imported.customQueries || {});
      setFavoriteQueries(imported.favoriteQueries || {});
      setCustomFavoriteNames(imported.customFavoriteNames || {});
      setEventFilterSettings(imported.eventFilterSettings || {});
      setTimeInterval(imported.timeInterval || "24h");
      setApplyTimeFilter(imported.applyTimeFilter || false);
      if (imported.showGuide !== undefined) {
        setShowGuide(imported.showGuide);
      }
      if (imported.blueTiles) {
        setBlueTiles(imported.blueTiles);
      }
      if (imported.buttonPositions) {
        setButtonPositions(imported.buttonPositions);
      }
    } catch (error) {
      console.error(
        "Import error – please check that the JSON is valid.",
        error
      );
    }
  };

  const handleDownloadSettings = () => {
    const settings = {
      tables,
      selectedTables,
      customQueries,
      favoriteQueries,
      customFavoriteNames,
      eventFilterSettings,
      timeInterval,
      applyTimeFilter,
      showGuide,
      blueTiles,
      buttonPositions,
      editedQueries, // Persist custom queries in browser
    };
    const blob = new Blob([JSON.stringify(settings, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "queryBuilderSettings.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  const clearAllSettings = () => {
    localStorage.clear();
    setTables(initialTables);
    setSelectedTables([]);
    setCustomQueries({});
    setFavoriteQueries({});
    setCustomFavoriteNames({});
    setEventFilterSettings({});
    setTimeInterval("24h");
    setApplyTimeFilter(false);
    setShowGuide(true);
    setBlueTiles(defaultBlueTiles);
    setButtonPositions(defaultButtonPositions);
  };

  const handleLoadFromURL = () => {
    if (!settingsURL.trim()) {
      console.error("Please enter a valid URL.");
      return;
    }
    fetch(settingsURL)
      .then((res) => res.json())
      .then((imported) => {
        setTables(imported.tables || initialTables);
        setSelectedTables(imported.selectedTables || []);
        setCustomQueries(imported.customQueries || {});
        setFavoriteQueries(imported.favoriteQueries || {});
        setCustomFavoriteNames(imported.customFavoriteNames || {});
        setEventFilterSettings(imported.eventFilterSettings || {});
        setTimeInterval(imported.timeInterval || "24h");
        setApplyTimeFilter(imported.applyTimeFilter || false);
        if (imported.showGuide !== undefined) {
          setShowGuide(imported.showGuide);
        }
        if (imported.blueTiles) {
          setBlueTiles(imported.blueTiles);
        }
        if (imported.buttonPositions) {
          setButtonPositions(imported.buttonPositions);
        }
      })
      .catch((error) => {
        console.error("Failed to load settings from URL.", error);
      });
  };

  

  useEffect(() => {
    if (modalOpen && modalTextRef.current) {
      modalTextRef.current.focus();
      modalTextRef.current.select();
    }
  }, [modalOpen, modalQueryText]);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") {
        setFavoriteDropdown({});
        setShowPopup(false);
        closeModal();
        setTagModalData({
          visible: false,
          tableName: "",
          queryIndex: null,
          customName: "",
          tags: [],
        });
        setEventFilterModal((prev) => ({
          ...prev,
          visible: false,
          eventTime: new Date().toISOString(),
          offsetBefore: 2,
          offsetAfter: 2,
        }));
        setShowAddTileModal(false);
      }
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isTileResizing || !tileModalRef.current) return;
      const rect = tileModalRef.current.getBoundingClientRect();
      const newWidth = Math.max(800, e.clientX - rect.left);
      const newHeight = Math.max(600, e.clientY - rect.top);
      setTileModalSize({ width: newWidth, height: newHeight });
    };
    const handleMouseUp = () => {
      if (isTileResizing) setIsTileResizing(false);
    };
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isTileResizing]);

  const addNewTable = () => {
    if (!newTableName.trim()) return;
    const tableExists = tables.some((t) => t.name === newTableName);
    if (!tableExists) {
      setTables([
        ...tables,
        { name: newTableName, comment: `// table name_is_${newTableName}` },
      ]);
    }
    if (newTableQuery.trim()) {
      setCustomQueries((prev) => {
        if (prev[newTableName]) {
          return {
            ...prev,
            [newTableName]: prev[newTableName] + "\n" + newTableQuery.trim(),
          };
        }
        return { ...prev, [newTableName]: newTableQuery.trim() };
      });
    }
    setSelectedTables((prev) =>
      prev.includes(newTableName) ? prev : [...prev, newTableName]
    );
    setNewTableName("");
    setNewTableQuery("");
  };

  // Function to check if a query contains only empty quotes, replacing them with the searchValue.
  const getBaseQuery = (tableName) => {
    let query = "";
    if (customQueries[tableName]) {
      query = customQueries[tableName].trim();
      query = query.replace(
        /\| search\s*""/g,
        `| search ${ensureKqlQuoted(searchValue)}`
      );
    } else {
      query = `${tableName}\n| search ${ensureKqlQuoted(searchValue)}`;
    }
    if (searchValue.trim() !== "") {
      query = query.replace(/(^|\n)\s*""\s*(?=\n|$)/g, `$1"${searchValue}"`);
    }
    if (query.trim() === '""') {
      query = `"${searchValue}"`;
    }
    return query;
  };

  // Modified getQueryWithTimeFilter function with EventTime filtering.
  // The extend clause is placed after the table name; lines are joined with newline ("\n").
  // Rewritten: getQueryWithTimeFilter — safe and complete
  const getQueryWithTimeFilter = (query, tableName, queryIndex) => {
    const settings =
      eventFilterSettings[tableName] &&
      eventFilterSettings[tableName][queryIndex];

    if (settings && settings.eventTime) {
      const { eventTime, offsetBefore = 0, offsetAfter = 0 } = settings;
      const toMin = (v) => {
        const n = parseFloat(String(v).replace(/[^0-9.-]/g, ""));
        return isNaN(n) ? 0 : n;
      };
      const ob = toMin(offsetBefore);
      const oa = toMin(offsetAfter);

      const letClause = `let Time = datetime(${eventTime});`;
      const tableLine = `${tableName}`;
      const extendClause = `| extend EventTime = coalesce(column_ifexists("Timestamp", datetime(null)), column_ifexists("TimeGenerated", datetime(null)))`;
      const searchLine = `| search ${ensureKqlQuoted(searchValue)}`;
      const whereLine = `| where EventTime between (datetime_add('minute', -${ob}, Time) .. datetime_add('minute', ${oa}, Time))`;

      return [letClause, tableLine, extendClause, searchLine, whereLine].join(
        "\n"
      );
    } else if (applyTimeFilter) {
      let newQuery = query || "";
      // Only add the where filter if it's not already present
      if (!/\|\s*where\s+TimeGenerated\s*>=\s*ago\(/i.test(newQuery)) {
        const m = newQuery.match(/\|[ \t]*search[^\n]*/);
        if (m) {
          const idx = newQuery.indexOf(m[0]) + m[0].length;
          newQuery =
            newQuery.slice(0, idx) +
            `\n| where TimeGenerated >= ago(${timeInterval})` +
            newQuery.slice(idx);
        } else {
          newQuery = `${newQuery.trim()}\n| where TimeGenerated >= ago(${timeInterval})`;
        }
      }
      // Replace placeholder | search "" with real search preserving quotes
      if (searchValue.trim() !== "") {
        newQuery = newQuery.replace(
          /\|\s*search\s*""/g,
          `| search ${ensureKqlQuoted(searchValue)}`
        );
      }
      return newQuery;
    }

    return query;
  };

  const getFullQuery = (tableName, queryIndex, baseQuery) => {
    const queryWithTime = getQueryWithTimeFilter(
      baseQuery,
      tableName,
      queryIndex
    );
    return queryWithTime.trim();
  };

  const generateQuery = () => {
    return selectedTables.flatMap((tableName) => {
      const tableSource = customQueries[tableName];
      const queries = (typeof tableSource === "string" && tableSource.includes("////")
        ? tableSource.split("////")
        : [getBaseQuery(tableName)])
        .map((q) => String(q || "").trim())
        .filter((q) => q);
      return queries.map((query, queryIndex) => {
          const replacedQuery = query.replace(
            "paste_data_from_user_here",
            searchValue
          );
          const fullQuery = getFullQuery(tableName, queryIndex, replacedQuery);
          return (
            <div
              key={`query-${tableName}-${queryIndex}`}
              id={`query-${tableName}-${queryIndex}`}
              className="relative bg-gray-800 text-white mb-6 rounded-lg shadow-md cursor-pointer p-3"
              style={{ marginTop: "8px", overflowX: "auto" }}
              onClick={() =>
                navigator.clipboard.writeText(
                  editedQueries[getQueryKey(tableName, queryIndex)] || fullQuery
                )
              }
            >
              {/* Inline editor for the whole query */}
              {editingMap[
                getQueryKey(
                  tableName,
                  typeof queryIndex !== "undefined" ? queryIndex : 0
                )
              ] ? (
                <div className="relative border border-yellow-500 rounded-md p-2 mt-2 bg-gray-900">
                  <textarea
                    value={
                      draftQueries[
                        getQueryKey(
                          tableName,
                          typeof queryIndex !== "undefined" ? queryIndex : 0
                        )
                      ] ??
                      editedQueries[
                        getQueryKey(
                          tableName,
                          typeof queryIndex !== "undefined" ? queryIndex : 0
                        )
                      ] ??
                      fullQuery
                    }
                    onChange={(e) =>
                      setEditedText(
                        getQueryKey(
                          tableName,
                          typeof queryIndex !== "undefined" ? queryIndex : 0
                        ),
                        e.target.value
                      )
                    }
                    className="w-full h-56 p-3 rounded bg-gray-800 border border-gray-600 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                  <div className="mt-2 flex gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const key = getQueryKey(
                          tableName,
                          typeof queryIndex !== "undefined" ? queryIndex : 0
                        );
                        const text =
                          draftQueries[key] ?? editedQueries[key] ?? fullQuery;
                        saveCustomQuery(
                          tableName,
                          typeof queryIndex !== "undefined" ? queryIndex : 0,
                          key,
                          text
                        );
                      }}
                      className="px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded text-xs"
                      title="Save Custom Query"
                    >
                      Save Custom Query
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const key = getQueryKey(
                          tableName,
                          typeof queryIndex !== "undefined" ? queryIndex : 0
                        );
                        toggleEditFor(key);
                      }}
                      className="px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded text-xs"
                      title="Cancel edit"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <CodeWithGutterEditor
                  baseText={fullQuery}
                  queryKey={getQueryKey(
                    tableName,
                    typeof queryIndex !== "undefined" ? queryIndex : 0
                  )}
                  onChange={handleEditedChange}
                  searchValue={searchValue}
                />
              )}

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigator.clipboard.writeText(
                    editedQueries[getQueryKey(tableName, queryIndex)] ||
                      fullQuery
                  );
                }}
                className="absolute top-2 right-2 bg-gray-700 text-white px-3 py-1 rounded-md hover:bg-gray-600 text-xs"
              >
                Copy
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleFavoriteQuery(tableName, queryIndex);
                }}
                className="absolute top-2 right-14 bg-gray-700 text-white px-3 py-1 rounded-md hover:bg-gray-600 text-xs"
                title="Add to Favorites"
              >
                {favoriteQueries[tableName]?.includes(queryIndex) ? "❤️" : "🤍"}
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  openExpandModal(tableName, queryIndex, query);
                }}
                className="absolute top-2 right-28 bg-gray-700 text-white px-3 py-1 rounded-md hover:bg-gray-600 text-xs"
              >
                🔍
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleEditFor(getQueryKey(tableName, queryIndex));
                }}
                className="absolute top-2 right-56 bg-gray-700 text-white px-3 py-1 rounded-md hover:bg-gray-600 text-xs"
                title="Edit Query"
              >
                ✏️
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  openEventFilterModal(tableName, queryIndex);
                }}
                className="absolute top-2 right-40 bg-gray-700 text-white px-3 py-1 rounded-md hover:bg-gray-600 text-xs"
                title="Set event time filter"
              >
                ⏱
              </button>
              <div className="absolute top-2 right-72 flex items-center bg-gray-700 text-white rounded-md">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setApplyTimeFilter((prev) => !prev);
                  }}
                  className="px-3 py-1 border-r border-gray-600 hover:bg-gray-600 text-xs"
                  title="Toggle classic time filter"
                >
                  ⏰
                </button>
                <input
                  type="text"
                  value={timeInterval}
                  onChange={(e) => {
                    e.stopPropagation();
                    setTimeInterval(e.target.value);
                  }}
                  className="bg-transparent text-xs p-1 w-28 focus:outline-none"
                  placeholder="(7d)(3h)(3m)"
                />
              </div>
            </div>
          );
        });

    });
  };

  // Drag and drop functions for blueTiles
  const handleTileDragStart = (e, index) => {
    dragTileItem.current = index;
    e.dataTransfer.effectAllowed = "move";
    setTimeout(() => {
      e.target.classList.add("opacity-50");
    }, 0);
  };

  const handleTileDragEnter = (e, index) => {
    dragOverTileItem.current = index;
    e.preventDefault();
    e.target.classList.add("bg-gray-700");
  };

  const handleTileDragLeave = (e) => {
    e.target.classList.remove("bg-gray-700");
  };

  const handleTileDragOver = (e) => {
    e.preventDefault();
  };

  const handleTileDragEnd = (e) => {
    e.target.classList.remove("opacity-50");
  };

  const handleTileDrop = (e, index) => {
    e.stopPropagation();
    const newTiles = [...blueTiles];
    const draggedItem = newTiles.splice(dragTileItem.current, 1)[0];
    newTiles.splice(index, 0, draggedItem);
    setBlueTiles(newTiles);
    e.target.classList.remove("bg-gray-700");
    dragTileItem.current = null;
    dragOverTileItem.current = null;
  };

  const renderSmartTiles = () => {
    return blueTiles.map((tile, index) => {
      // We keep the big "Search Around Event" button above the tiles.
      // If an old state still contains the small tile, hide it here.
      if (tile.id === "tile-around") return null;
      let finalQuery = "";
      if (tile.queryTemplate) {
        finalQuery = tile.queryTemplate
          .replace("{searchValue}", searchValue)
          .replace(
            /\|\s*search\s*\"\"/g,
            `| search ${ensureKqlQuoted(searchValue)}`
          );
      } else if (tile.query) {
        finalQuery = tile.query.replace(
          /\|\s*search\s*\"\"/g,
          `| search ${ensureKqlQuoted(searchValue)}`
        );
      }
      if (searchValue.trim() !== "") {
        finalQuery = finalQuery.replace(
          /(^|\n)\s*\"\"(?=\n|$)/g,
          `$1\"${searchValue}\"`
        );
      }
      if (finalQuery.trim() === '""') {
        finalQuery = `\"${searchValue}\"`;
      }
      return (
        <div
          key={tile.id}
          className="relative inline-block"
          {...(tile.isPlus
            ? {}
            : {
                draggable: true,
                onDragStart: (e) => handleTileDragStart(e, index),
                onDragEnter: (e) => handleTileDragEnter(e, index),
                onDragLeave: handleTileDragLeave,
                onDragOver: handleTileDragOver,
                onDragEnd: handleTileDragEnd,
                onDrop: (e) => handleTileDrop(e, index),
              })}
        >
          <button
            onClick={() => {
              const processedQuery = newTileQuery.replace(
                /\|\s*search\s*""/g,
                `| search ${ensureKqlQuoted(searchValue)}`
              );
              if (tile.isPlus) {
                openAddTileModal();
              } else {
                navigator.clipboard.writeText(finalQuery);
              }
            }}
            onDoubleClick={() => {
              if (!tile.isPredefined && !tile.isPlus) {
                openAddTileModal(tile);
              }
            }}
            className="w-44 py-2 px-3 bg-blue-400 hover:bg-blue-500 text-white text-sm font-medium rounded-lg shadow transition"
            title={tile.label}
          >
            {tile.label}
          </button>
        </div>
      );
    });
  };

  const handleDragStartWrapper = (e, position, tableList) => {
    dragItem.current = position;
    e.dataTransfer.effectAllowed = "move";
    setTimeout(() => {
      e.target.classList.add("opacity-50");
    }, 0);
  };

  const handleDragEnter = (e, position) => {
    dragOverItem.current = position;
    e.preventDefault();
    e.target.classList.add("bg-gray-700");
  };

  const handleDragLeave = (e) => {
    e.target.classList.remove("bg-gray-700");
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDragEnd = (e, tableList, setTableList) => {
    e.target.classList.remove("opacity-50");
    if (dragItem.current === dragOverItem.current) return;
    const _tableList = [...tableList];
    const draggedItem = _tableList.splice(dragItem.current, 1)[0];
    _tableList.splice(dragOverItem.current, 0, draggedItem);
    dragItem.current = null;
    dragOverItem.current = null;
    setTableList(_tableList);
  };

  const removeTable = (tableName) => {
    setTables((prev) => prev.filter((t) => t.name !== tableName));
    setSelectedTables((prev) => prev.filter((t) => t !== tableName));
  };

  function closeModal() {
    setModalOpen(false);
    setModalQueryText("");
  }

  const toggleSettingsPanel = () => {
    setShowSettingsPanel((prev) => {
      const newState = !prev;
      if (newState) {
        setTimeout(() => {
          settingsRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 100);
      }
      return newState;
    });
  };

  const openExpandModal = (tableName, queryIndex, baseQuery) => {
    setCurrentModalKey(getQueryKey(tableName, queryIndex));
    const fullQuery = getFullQuery(tableName, queryIndex, baseQuery);
    const _editedKey = getQueryKey(tableName, queryIndex);
    const _editedText = editedQueries[_editedKey];
    setModalQueryText(_editedText || fullQuery);
    setModalOpen(true);
  };

  const openEventFilterModal = (tableName, queryIndex) => {
    const existing =
      eventFilterSettings[tableName] &&
      eventFilterSettings[tableName][queryIndex];
    if (existing) {
      setEventFilterSettings((prev) => {
        const newSettings = { ...prev };
        if (newSettings[tableName]) {
          delete newSettings[tableName][queryIndex];
          if (Object.keys(newSettings[tableName]).length === 0)
            delete newSettings[tableName];
        }
        return newSettings;
      });
      return;
    } else {
      setEventFilterModal({
        visible: true,
        tableName,
        queryIndex,
        eventTime: new Date().toISOString(),
        offsetBefore: 2,
        offsetAfter: 2,
      });
    }
  };

  const handleSaveEventFilterModal = () => {
    const { tableName, queryIndex, eventTime, offsetBefore, offsetAfter } =
      eventFilterModal;
    setEventFilterSettings((prev) => ({
      ...prev,
      [tableName]: {
        ...prev[tableName],
        [queryIndex]: { eventTime, offsetBefore, offsetAfter },
      },
    }));
    setEventFilterModal({
      visible: false,
      tableName: "",
      queryIndex: null,
      eventTime: new Date().toISOString(),
      offsetBefore: 2,
      offsetAfter: 2,
    });
  };

  const handleCancelEventFilterModal = () => {
    setEventFilterModal({
      visible: false,
      tableName: "",
      queryIndex: null,
      eventTime: new Date().toISOString(),
      offsetBefore: 2,
      offsetAfter: 2,
    });
  };

  const openAddTileModal = (tile = null) => {
    if (tile) {
      setEditingTile(tile);
      setNewTileName(tile.label);
      setNewTileQuery(tile.queryTemplate || tile.query || "");
    } else {
      setEditingTile(null);
      setNewTileName("");
      setNewTileQuery("");
    }
    setShowAddTileModal(true);
  };

  return (
    <div className="min-h-screen flex flex-col">
      {showAroundPopup && (
        <div
          onClick={() => setShowAroundPopup(false)}
          className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            ref={aroundModalRef}
            role="dialog"
            aria-modal="true"
            tabIndex={-1}
            className="bg-white p-5 rounded-lg shadow-lg w-[90vw] max-w-5xl max-h-[85vh] overflow-auto relative"
          >
            <button
              onClick={() => setShowAroundPopup(false)}
              className="absolute top-4 right-4 bg-white border border-gray-400 rounded-full w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-gray-100 shadow"
              aria-label="Close"
            >
              &#x2715;
            </button>
            <h3 className="text-xl font-bold mb-4">Search Around Event</h3>
            {showAroundQuery && (
              <div className="mb-4">
                <textarea
                  ref={aroundQueryRef}
                  readOnly
                  value={aroundQueryText}
                  className="w-full p-2 border border-gray-300 rounded text-sm font-mono select-all"
                />
              </div>
            )}
            <label className="block mb-2">Mean Event Keyword:</label>
            <input
              type="text"
              placeholder="FileName.ps1 / ProcessName etc"
              value={aroundConfig.meanEvent}
              onChange={(e) =>
                setAroundConfig((prev) => ({
                  ...prev,
                  meanEvent: e.target.value,
                }))
              }
              className="w-full p-2 border border-gray-300 rounded mb-4"
            />
            <label className="block mb-2">Context Filter Keyword:</label>
            <input
              type="text"
              placeholder="Please write the uniq hostname or username etc..."
              value={aroundConfig.contextFilter}
              onChange={(e) =>
                setAroundConfig((prev) => ({
                  ...prev,
                  contextFilter: e.target.value,
                }))
              }
              className="w-full p-2 border border-gray-300 rounded mb-4"
            />
            <label className="block mb-2">Event Time:</label>
            <div className="relative mb-4 space-y-2">
              {/* Text input OR picker — both keep the same state */}
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  placeholder="e.g., 2025-10-03T14:54:07.599Z"
                  value={aroundConfig.eventTime}
                  onChange={(e) =>
                    setAroundConfig((prev) => ({
                      ...prev,
                      eventTime: e.target.value,
                    }))
                  }
                  className="flex-1 p-2 border border-gray-300 rounded"
                />
                <input
                  ref={dateGuiRef}
                  type="datetime-local"
                  value={isoToLocalInput(aroundConfig.eventTime)}
                  onChange={(e) =>
                    setAroundConfig((prev) => ({
                      ...prev,
                      eventTime: localInputToIso(e.target.value),
                    }))
                  }
                  className="p-2 border border-gray-300 rounded"
                />
                <button
                  type="button"
                  onClick={() => {
                    try {
                      dateGuiRef.current?.showPicker?.();
                    } catch (e) {}
                  }}
                  className="px-3 py-2 bg-gray-200 rounded hover:bg-gray-300"
                  title="Open date & time picker"
                >
                  🗓️
                </button>
              </div>

              <label className="block mb-2">Minutes Before:</label>
              <input
                type="number"
                value={String(aroundConfig.offsetBefore ?? "")}
                onChange={(e) =>
                  setAroundConfig((prev) => ({
                    ...prev,
                    offsetBefore: e.target.value,
                  }))
                }
                className="w-full p-2 border border-gray-300 rounded"
              />
              <label className="block mb-2">Minutes After:</label>
              <input
                type="number"
                value={String(aroundConfig.offsetAfter ?? "")}
                onChange={(e) =>
                  setAroundConfig((prev) => ({
                    ...prev,
                    offsetAfter: e.target.value,
                  }))
                }
                className="w-full p-2 border border-gray-300 rounded"
              />
            </div>
            <div className="mb-4 flex space-x-2">
              <input
                type="text"
                placeholder="Custom table name"
                value={customTableName}
                onChange={(e) => setCustomTableName(e.target.value)}
                className="flex-1 p-2 border border-gray-300 rounded"
              />
              <button
                onClick={() => {
                  const processedQuery = newTileQuery.replace(
                    /\|\s*search\s*""/g,
                    `| search ${ensureKqlQuoted(searchValue)}`
                  );
                  const name = customTableName.trim();
                  if (!name) return;
                  // Build and copy a query for ONLY this custom table
                  const {
                    meanEvent,
                    contextFilter,
                    repeatMeanEvent,
                    eventTime,
                    offsetBefore,
                    offsetAfter,
                  } = aroundConfig;
                  const toMin = (v) => {
                    const n = parseFloat(String(v).replace(/[^0-9.-]/g, ""));
                    return isNaN(n) ? 0 : n;
                  };
                  const offB = toMin(offsetBefore);
                  const offA = toMin(offsetAfter);
                  const q = `let Time = datetime(${eventTime});
  search in (${name}) *
  | extend EventTime = coalesce(column_ifexists("Timestamp", datetime(null)), column_ifexists("TimeGenerated", datetime(null)))
  | where EventTime between (
                  datetime_add('minute', -${offB}, Time) ..
                  datetime_add('minute', ${offA}, Time)
              )
  | extend EventTime = coalesce(column_ifexists("Timestamp", datetime(null)), column_ifexists("TimeGenerated", datetime(null)))
  | where EventTime between (datetime_add('minute', -${offB}, Time) .. datetime_add('minute', ${offA}, Time))
  | extend EventLabel = case(
      * has "${meanEvent}", "🟥 Alert Event",
      (* has "${contextFilter}" and not(* has "${meanEvent}")), "🟦 Context Event",
      ""
  )
  | where EventLabel != ""
  | project-reorder EventLabel, *`;
                  navigator.clipboard.writeText(q);
                  setShowAroundPopup(false);
                  setCustomTableName("");
                }}
                className="px-4 py-2 bg-blue-500 text-white rounded text-sm"
              >
                Copy with Custom Table
              </button>
            </div>
            {/* Toggle select/check all tables */}
            <button
              onClick={() => {
                const processedQuery = newTileQuery.replace(
                  /\|\s*search\s*""/g,
                  `| search ${ensureKqlQuoted(searchValue)}`
                );
                if (selectedUnionTables.length > 0) {
                  setSelectedUnionTables([]);
                } else {
                  setSelectedUnionTables(tables.map((t) => t.name));
                }
              }}
              className="mb-4 px-4 py-2 bg-yellow-500 text-white rounded text-sm"
            >
              {selectedUnionTables.length > 0 ? "Uncheck All" : "Check All"}
            </button>
            <button
              onClick={() => {
                const processedQuery = newTileQuery.replace(
                  /\|\s*search\s*""/g,
                  `| search ${ensureKqlQuoted(searchValue)}`
                );
                // Deselect all specific tables
                setSelectedUnionTables([]);
                // Generate global search query
                const {
                  meanEvent,
                  contextFilter,
                  eventTime,
                  offsetBefore,
                  offsetAfter,
                } = aroundConfig;
                const toMin = (v) => {
                  const n = parseFloat(String(v).replace(/[^0-9.-]/g, ""));
                  return isNaN(n) ? 0 : n;
                };
                const offB = toMin(offsetBefore);
                const offA = toMin(offsetAfter);
                const q = `let Time = datetime(${eventTime});
              search *
              | extend EventTime = coalesce(column_ifexists("Timestamp", datetime(null)), column_ifexists("TimeGenerated", datetime(null)))
              | where EventTime between (
                  datetime_add('minute', -${offB}, Time) ..
                  datetime_add('minute', ${offA}, Time)
              )
              | extend EventTime = coalesce(column_ifexists("Timestamp", datetime(null)), column_ifexists("TimeGenerated", datetime(null)))
              | where EventTime between (datetime_add('minute', -${offB}, Time) .. datetime_add('minute', ${offA}, Time))
              | extend EventLabel = case(
                  * has "${meanEvent}", "🟥 Alert Event",
                  (* has "${contextFilter}" and not(* has "${meanEvent}")), "🟦 Context Event",
                  ""
              )
              | where EventLabel != ""
              | project-reorder EventLabel, *`;
                navigator.clipboard.writeText(q);
                setShowAroundPopup(false);
                setCustomTableName("");
              }}
              className="mb-4 px-4 py-2 bg-green-500 text-white rounded text-sm"
            >
              Search All Tables Not Specified
            </button>
            {/* ─── available tables ────────────────────────────────────── */}
            <label className="block font-medium mb-1">available tables:</label>
            <div className="mb-4 max-h-40 overflow-y-auto border border-gray-200 rounded p-2">
              {tables.map((t) => (
                <label
                  key={t.name}
                  className="flex items-center space-x-2 mb-1"
                >
                  <input
                    type="checkbox"
                    checked={selectedUnionTables.includes(t.name)}
                    onChange={() =>
                      setSelectedUnionTables((prev) =>
                        prev.includes(t.name)
                          ? prev.filter((x) => x !== t.name)
                          : [...prev, t.name]
                      )
                    }
                    className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                  />
                  <span className="text-sm">{t.name}</span>
                </label>
              ))}
            </div>
            {/* ─────────────────────────────────────────────────────────────────────── */}
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => {
                  const processedQuery = newTileQuery.replace(
                    /\|\s*search\s*""/g,
                    `| search ${ensureKqlQuoted(searchValue)}`
                  );
                  const q = generateUnionQuery();
                  setModalQuery(q);
                  setShowQueryModal(true);
                }}
                className="px-4 py-2 bg-blue-500 text-white rounded text-sm"
              >
                Show Query
              </button>
              <button
                onClick={() => {
                  const processedQuery = newTileQuery.replace(
                    /\|\s*search\s*""/g,
                    `| search ${ensureKqlQuoted(searchValue)}`
                  );
                  const q = generateUnionQuery();
                  navigator.clipboard.writeText(q);
                  setShowAroundPopup(false);
                  setShowAroundQuery(false);
                }}
                className="px-4 py-2 bg-indigo-600 text-white rounded text-sm"
              >
                Generate & Copy
              </button>
              <button
                onClick={() => {
                  const processedQuery = newTileQuery.replace(
                    /\|\s*search\s*""/g,
                    `| search ${ensureKqlQuoted(searchValue)}`
                  );
                  setShowAroundPopup(false);
                  setShowAroundQuery(false);
                }}
                className="px-4 py-2 bg-gray-600 text-white rounded text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Global custom dark scrollbar styles */}
      <GlobalScrollbarStyles />
      <ResponsiveShellStyles />

      {showAddCharsModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
          onClick={() => setShowAddCharsModal(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full"
          >
            <h3 className="text-xl font-bold mb-4">
              Add Characters to Each Line
            </h3>
            <label className="block text-sm mb-1">
              Add to beginning of each line:
            </label>
            <input
              type="text"
              value={addCharsBegin}
              onChange={(e) => setAddCharsBegin(e.target.value)}
              className="w-full p-2 border rounded mb-2"
            />
            <label className="block text-sm mb-1">
              Add to ending of each line:
            </label>
            <input
              type="text"
              value={addCharsEnd}
              onChange={(e) => setAddCharsEnd(e.target.value)}
              className="w-full p-2 border rounded mb-2"
            />
            <label className="block text-sm mb-1">Paste text here:</label>
            <textarea
              value={addCharsInput}
              onChange={(e) => setAddCharsInput(e.target.value)}
              className="w-full p-2 border rounded h-24 mb-2"
            />
            <label className="block text-sm mb-1">Preview Output:</label>
            <textarea
              readOnly
              value={computeAddCharsOutput()}
              className="w-full p-2 border rounded h-24 mb-4"
            />
            <button
              onClick={() =>
                navigator.clipboard.writeText(computeAddCharsOutput())
              }
              className="w-full py-2 bg-indigo-600 rounded mb-4 text-white"
            >
              Copy Output
            </button>
            <button
              onClick={() => setShowAddCharsModal(false)}
              className="px-4 py-2 bg-gray-600 text-white rounded float-right"
            >
              Close
            </button>
          </div>
        </div>
      )}
      {showDecodeUrlModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
          onClick={() => setShowDecodeUrlModal(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full"
          >
            <h3 className="text-xl font-bold mb-4">Decode URL</h3>
            <label className="block text-sm mb-1">Paste encoded URL:</label>
            <textarea
              value={decodeUrlInput}
              onChange={(e) => setDecodeUrlInput(e.target.value)}
              className="w-full p-2 border rounded h-20 mb-2"
            />
            <label className="block text-sm mb-1">Decoded Output:</label>
            <textarea
              readOnly
              value={getDecodedUrl()}
              className="w-full p-2 border rounded h-20 mb-4"
            />
            <button
              onClick={() => navigator.clipboard.writeText(getDecodedUrl())}
              className="w-full py-2 bg-indigo-600 rounded mb-4 text-white"
            >
              Copy Output
            </button>
            <button
              onClick={() => setShowDecodeUrlModal(false)}
              className="px-4 py-2 bg-gray-600 text-white rounded float-right"
            >
              Close
            </button>
          </div>
        </div>
      )}
      {showDecodeBase64Modal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
          onClick={() => setShowDecodeBase64Modal(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full"
          >
            <h3 className="text-xl font-bold mb-4">Decode Base64</h3>
            <label className="block text-sm mb-1">Paste Base64 text:</label>
            <textarea
              value={decodeBase64Input}
              onChange={(e) => setDecodeBase64Input(e.target.value)}
              className="w-full p-2 border rounded h-20 mb-2"
            />
            <label className="block text-sm mb-1">Decoded Output:</label>
            <textarea
              readOnly
              value={getDecodedBase64()}
              className="w-full p-2 border rounded h-20 mb-4"
            />
            <button
              onClick={() => navigator.clipboard.writeText(getDecodedBase64())}
              className="w-full py-2 bg-indigo-600 rounded mb-4 text-white"
            >
              Copy Output
            </button>
            <button
              onClick={() => setShowDecodeBase64Modal(false)}
              className="px-4 py-2 bg-gray-600 text-white rounded float-right"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {showPasteEventTimeModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
          onClick={() => setShowPasteEventTimeModal(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white p-6 rounded-lg shadow-lg max-w-3xl w-[90vw] max-h-[85vh] overflow-auto"
          >
            <h3 className="text-xl font-bold mb-4">Set Event Time Filter</h3>

            <div className="mb-4">
              <div className="text-sm font-semibold mb-2">Platform</div>
              <div className="flex flex-wrap gap-3">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="pastePlatform"
                    value="defender"
                    checked={pastePlatform === "defender"}
                    onChange={() => setPastePlatform("defender")}
                  />
                  Defender Advanced Hunting
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="pastePlatform"
                    value="sentinel"
                    checked={pastePlatform === "sentinel"}
                    onChange={() => setPastePlatform("sentinel")}
                  />
                  Sentinel (Log Analytics)
                </label>
              </div>
              <div className="text-xs text-gray-600 mt-2 font-mono break-all">
                {pasteEventTime}
              </div>
            </div>

            <label className="block text-sm mb-1">Paste your KQL query:</label>
            <textarea
              value={pastedKqlInput}
              onChange={(e) => setPastedKqlInput(e.target.value)}
              className="w-full p-2 border rounded h-40 mb-4 font-mono text-sm"
              placeholder="Paste full KQL here"
            />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3 items-start">
              <div>
                <label className="block text-sm mb-1">Date and Time</label>

                <div className="flex gap-2">
                  <input
                    type="datetime-local"
                    value={pasteEventTimeLocal}
                    onChange={(e) => {
                      const v = e.target.value;
                      setPasteEventTimeLocal(v);
                      const iso = datetimeLocalToIsoZ(v);
                      if (iso) setPasteEventTimeIsoZ(iso);
                      const kql = datetimeLocalToKqlDatetime(v);
                      if (kql) setPasteEventTime(kql);
                    }}
                    className="flex-1 p-2 border rounded h-10"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const now = new Date();
                      const nowKql = dateToKqlDatetime(now);
                      setPasteEventTime(nowKql);
                      const local = kqlDatetimeToDatetimeLocal(nowKql);
                      setPasteEventTimeLocal(local);
                      setPasteEventTimeIsoZ(now.toISOString());
                      setPasteEventTimeFromLogs("");
                    }}
                    className="w-20 h-10 bg-gray-200 rounded text-sm"
                    title="Set current time"
                  >
                    Now
                  </button>
                </div>

                <div className="text-xs text-gray-600 mt-1 font-mono break-all h-4">
                  {pasteEventTime}
                </div>
              </div>
              <div>
                <label className="block text-sm mb-1">
                  Offset Before (minutes):
                </label>
                <input
                  type="number"
                  value={pasteOffsetBefore}
                  onChange={(e) => setPasteOffsetBefore(e.target.value)}
                  className="w-full p-2 border rounded h-10"
                />
                <div className="text-xs text-transparent mt-1 h-4">.</div>
              </div>
              <div>
                <label className="block text-sm mb-1">
                  Offset After (minutes):
                </label>
                <input
                  type="number"
                  value={pasteOffsetAfter}
                  onChange={(e) => setPasteOffsetAfter(e.target.value)}
                  className="w-full p-2 border rounded h-10"
                />
                <div className="text-xs text-transparent mt-1 h-4">.</div>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm mb-1">
                Paste date/time from Sentinel/Defender logs (optional):
              </label>
              <input
                type="text"
                value={pasteEventTimeFromLogs}
                onChange={(e) => {
                  const v = e.target.value;
                  setPasteEventTimeFromLogs(v);
                  const kqlFromDef =
                    parseDefenderDisplayedTimestampToKqlDatetime(v);
                  if (kqlFromDef) {
                    setPasteEventTime(kqlFromDef);
                    setPasteEventTimeLocal(
                      kqlDatetimeToDatetimeLocal(kqlFromDef)
                    );
                    const isoZ_ = datetimeLocalToIsoZ(
                      kqlDatetimeToDatetimeLocal(kqlFromDef)
                    );
                    if (isoZ_) setPasteEventTimeIsoZ(isoZ_);
                    return;
                  }
                  const iso = parseLogTimestampToIsoZ(v);
                  if (iso) setPasteEventTimeIsoZ(iso);
                  const kql = iso ? isoZToKqlDatetime(iso) : "";
                  if (kql) {
                    setPasteEventTime(kql);
                    setPasteEventTimeLocal(kqlDatetimeToDatetimeLocal(kql));
                  }
                }}
                onBlur={() => {
                  // if user pasted something that can be parsed, snap UI to it
                  const kqlFromDef =
                    parseDefenderDisplayedTimestampToKqlDatetime(
                      pasteEventTimeFromLogs
                    );
                  if (kqlFromDef) {
                    setPasteEventTime(kqlFromDef);
                    setPasteEventTimeLocal(
                      kqlDatetimeToDatetimeLocal(kqlFromDef)
                    );
                    const isoZ_ = datetimeLocalToIsoZ(
                      kqlDatetimeToDatetimeLocal(kqlFromDef)
                    );
                    if (isoZ_) setPasteEventTimeIsoZ(isoZ_);
                    return;
                  }
                  const iso = parseLogTimestampToIsoZ(pasteEventTimeFromLogs);
                  if (iso) setPasteEventTimeIsoZ(iso);
                  const kql = iso ? isoZToKqlDatetime(iso) : "";
                  if (kql) {
                    setPasteEventTime(kql);
                    setPasteEventTimeLocal(kqlDatetimeToDatetimeLocal(kql));
                  }
                }}
                placeholder="Examples: 2 Feb 2026 16:30:02 | 09.01.2026 08:04 | 09/01/2026, 08:04:00.000 | 2026-01-09 08:04:00"
                className="w-full p-2 border rounded h-10"
              />
              <div className="text-xs text-gray-500 mt-1">
                Tip: timestamps without timezone are treated as your local time
                (same as the GUI picker).
              </div>
            </div>

            <label className="block text-sm mb-1">Output query:</label>
            <textarea
              readOnly
              value={getPastedKqlWithEventTimeFilter()}
              className="w-full p-2 border rounded h-48 mb-4 font-mono text-sm"
            />

            <div className="flex flex-col md:flex-row gap-2">
              <button
                onClick={() =>
                  navigator.clipboard.writeText(
                    getPastedKqlWithEventTimeFilter()
                  )
                }
                className="w-full py-2 bg-indigo-600 rounded text-white"
              >
                Copy Output
              </button>
              <button
                onClick={() => setShowPasteEventTimeModal(false)}
                className="w-full md:w-auto px-4 py-2 bg-gray-600 text-white rounded"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showSaveQueryModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
          onClick={() => setShowSaveQueryModal(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white p-6 rounded-lg shadow-lg max-w-3xl w-[90vw] max-h-[85vh] overflow-auto"
          >
            <h3 className="text-xl font-bold mb-4">
              {editingSavedQueryId ? "Edit Saved Query" : "Save Query"}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm mb-1">Title (required)</label>
                <input
                  type="text"
                  value={saveQueryForm.title}
                  onChange={(e) =>
                    setSaveQueryForm((p) => ({ ...p, title: e.target.value }))
                  }
                  className="w-full p-2 border rounded h-10"
                  placeholder="e.g., Entra sign-ins – last 24h"
                />
              </div>
              <div>
                <label className="block text-sm mb-1">Tags (max 3)</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {(saveQueryForm.tags || []).map((t, index) => (
                    <div
                      key={index}
                      className="bg-yellow-200 text-yellow-700 px-2 py-1 rounded cursor-pointer select-none"
                      title="Click to remove"
                      onClick={() =>
                        setSaveQueryForm((p) => ({
                          ...p,
                          tags: (p.tags || []).filter((_, i) => i !== index),
                        }))
                      }
                    >
                      #{t}
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={saveQueryCurrentTag}
                    onChange={(e) => setSaveQueryCurrentTag(e.target.value)}
                    onKeyDown={handleSaveQueryTagKeyDown}
                    className="w-full p-2 border rounded h-10"
                    placeholder="e.g., mfa"
                    disabled={(saveQueryForm.tags || []).length >= 3}
                  />
                  <button
                    type="button"
                    onClick={addSaveQueryTag}
                    disabled={(saveQueryForm.tags || []).length >= 3}
                    className="px-4 py-2 bg-indigo-600 text-white rounded h-10 whitespace-nowrap disabled:opacity-50"
                  >
                    Add
                  </button>
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  Press Enter to add. Click a tag to remove.
                </div>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm mb-1">Description</label>
              <textarea
                value={saveQueryForm.description}
                onChange={(e) =>
                  setSaveQueryForm((p) => ({
                    ...p,
                    description: e.target.value,
                  }))
                }
                className="w-full p-2 border rounded h-20"
                placeholder="Optional notes: when to use, expected output, etc."
              />
            </div>

            <div className="flex items-center gap-2 mb-3">
              <input
                id="pinSavedQuery"
                type="checkbox"
                checked={saveQueryForm.pinned}
                onChange={(e) =>
                  setSaveQueryForm((p) => ({ ...p, pinned: e.target.checked }))
                }
              />
              <label htmlFor="pinSavedQuery" className="text-sm">
                Pin to top
              </label>
            </div>

            <div className="mb-4">
              <label className="block text-sm mb-1">
                KQL query or investigation notes (required)
              </label>
              <textarea
                value={saveQueryForm.query}
                onChange={(e) =>
                  setSaveQueryForm((p) => ({ ...p, query: e.target.value }))
                }
                onKeyDown={(e) => {
                  // Shift+Enter = Save & close (habit-friendly shortcut)
                  if (e.key === "Enter" && e.shiftKey) {
                    e.preventDefault();
                    saveOrUpdateSavedQuery();
                  }
                }}
                className="w-full p-2 border rounded h-64 font-mono text-sm"
                placeholder="Paste your KQL or investigation notes here"
              />
            </div>

            <div className="flex flex-col md:flex-row gap-2 justify-end">
              {editingSavedQueryId ? (
                <button
                  type="button"
                  onClick={() => {
                    deleteSavedQuery(editingSavedQueryId);
                    // make sure we don't keep stale edit state open
                    setEditingSavedQueryId(null);
                    setShowSaveQueryModal(false);
                  }}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded"
                  title="Delete this saved query / note"
                >
                  Delete
                </button>
              ) : null}

              <button
                onClick={() => {
                  saveOrUpdateSavedQuery();
                }}
                className="px-4 py-2 bg-indigo-600 text-white rounded"
                title="Tip: Shift+Enter also saves & closes"
              >
                {editingSavedQueryId ? "Update" : "Save"}
              </button>
              <button
                onClick={() => setShowSaveQueryModal(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showFindReplaceModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
          onClick={() => setShowFindReplaceModal(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full"
          >
            <h3 className="text-xl font-bold mb-4">Find and Replace</h3>
            <label className="block text-sm mb-1">Paste text here:</label>
            <textarea
              value={findReplaceInput}
              onChange={(e) => setFindReplaceInput(e.target.value)}
              className="w-full p-2 border rounded h-24 mb-2"
            />
            <div className="grid grid-cols-2 gap-2 mb-2">
              <div>
                <label className="block text-sm mb-1">Find:</label>
                <input
                  type="text"
                  value={findString}
                  onChange={(e) => setFindString(e.target.value)}
                  className="w-full p-2 border rounded"
                />
              </div>
              <div>
                <label className="block text-sm mb-1">Replace with:</label>
                <input
                  type="text"
                  value={replaceString}
                  onChange={(e) => setReplaceString(e.target.value)}
                  className="w-full p-2 border rounded"
                />
              </div>
            </div>
            <label className="block text-sm mb-1">Output Preview:</label>
            <textarea
              readOnly
              value={getFindReplaceOutput()}
              className="w-full p-2 border rounded h-24 mb-4"
            />
            <button
              onClick={() =>
                navigator.clipboard.writeText(getFindReplaceOutput())
              }
              className="w-full py-2 bg-indigo-600 rounded mb-4 text-white"
            >
              Copy Output
            </button>
            <button
              onClick={() => setShowFindReplaceModal(false)}
              className="px-4 py-2 bg-gray-600 text-white rounded float-right"
            >
              Close
            </button>
          </div>
        </div>
      )}
      {showEmailHeaderModal && (
        <div
          onClick={() => setShowEmailHeaderModal(false)}
          className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white p-6 rounded-lg shadow-lg max-w-2xl w-3/4 h-3/4 overflow-auto"
          >
            <h3 className="text-xl font-bold mb-4">
              Extract IPs from Email Header
            </h3>
            <label className="block text-sm mb-1">
              Paste Email Header here:
            </label>
            <textarea
              value={emailHeaderInput}
              onChange={(e) => {
                setEmailHeaderInput(e.target.value);
                extractIPsFromHeader(e.target.value);
              }}
              className="w-full p-2 border rounded h-32 mb-4"
            />
            {emailHeaderInput.trim() && (
              <>
                <label className="block text-sm mb-1"></label>

                {emailHeaderInput.trim() && (
                  <>
                    <div className="mb-4">
                      <p>
                        <strong>Subject:</strong> {emailSubject}
                      </p>
                      <p>
                        <strong>Sender:</strong> {emailSender}
                      </p>
                      <p>
                        <strong>Recipient:</strong> {emailRecipient}
                      </p>
                      <p>
                        <strong>Date and Time:</strong> {emailCreationTime}
                        <p>Extracted IPv4 IPs:</p>
                      </p>
                    </div>
                    <table className="w-full mb-4 border-collapse">
                      <thead>
                        <tr>
                          <th className="border px-2 py-1 text-left">
                            IP Address
                          </th>
                          <th className="border px-2 py-1 text-left">Type</th>
                          <th className="border px-2 py-1 text-left">
                            VirusTotal URL
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {extractedIPs.map((item) => (
                          <tr key={item.ip}>
                            <td className="border px-2 py-1">{item.ip}</td>
                            <td className="border px-2 py-1">{item.type}</td>
                            <td className="border px-2 py-1">
                              {item.url ? (
                                <a
                                  href={item.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  Open VirusTotal URL
                                </a>
                              ) : (
                                "-"
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}
              </>
            )}
            <button
              onClick={() => setShowEmailHeaderModal(false)}
              className="px-4 py-2 bg-gray-600 text-white rounded float-right mt-2"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {showNonClickableUrlModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
          onClick={() => setShowNonClickableUrlModal(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full"
          >
            <h3 className="text-xl font-bold mb-4">
              Convert to non-clickable URL
            </h3>
            <label className="block text-sm mb-1">Paste URL here:</label>
            <textarea
              value={nonClickableUrlInput}
              onChange={(e) => setNonClickableUrlInput(e.target.value)}
              className="w-full p-2 border rounded h-20 mb-2"
            />
            <label className="block text-sm mb-1">Converted Output:</label>
            <textarea
              readOnly
              value={convertNonClickableUrl()}
              className="w-full p-2 border rounded h-20 mb-4"
            />
            <button
              onClick={() =>
                navigator.clipboard.writeText(convertNonClickableUrl())
              }
              className="w-full py-2 bg-indigo-600 rounded mb-4 text-white"
            >
              Copy Output
            </button>
            <button
              onClick={() => setShowNonClickableUrlModal(false)}
              className="px-4 py-2 bg-gray-600 text-white rounded float-right"
            >
              Close
            </button>
          </div>
        </div>
      )}

      <div style={{ display: "none" }}>
        kql, kql search, microsoft, sc-200, kql generator, kql tool, security
        analyst, microsoft defender kql, sentinel, sentinel kql
      </div>
      {tagModalData.visible && (
        <div
          onClick={handleCloseModalOnClickOutside(() =>
            setTagModalData({
              visible: false,
              tableName: "",
              queryIndex: null,
              customName: "",
              tags: [],
            })
          )}
          className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white p-6 rounded-lg shadow-lg max-w-2xl w-full"
          >
            <h3 className="text-xl font-bold mb-4">Add Name / Tags</h3>
            <p className="text-sm text-gray-600 mb-4">
              Please add a short description which will be visible in the
              Favorites list, and you can also add up to 3 tags. You will be
              able to filter and search by keywords or tags.
            </p>
            <input
              type="text"
              placeholder="Name (optional)"
              value={tagModalData.customName}
              maxLength="40"
              onChange={(e) =>
                setTagModalData((prev) => ({
                  ...prev,
                  customName: e.target.value,
                }))
              }
              className="w-full p-2 border border-gray-300 rounded mb-4"
            />
            <div className="mb-4">
              <div className="flex flex-wrap gap-2 mb-2">
                {tagModalData.tags.map((t, index) => (
                  <div
                    key={index}
                    className="bg-yellow-200 text-yellow-700 px-2 py-1 rounded cursor-pointer"
                    onDoubleClick={() => handleEditTag(index)}
                  >
                    #{t}
                  </div>
                ))}
              </div>
              <div className="flex items-center">
                <input
                  type="text"
                  placeholder="Tag"
                  value={currentTag}
                  onChange={(e) => setCurrentTag(e.target.value)}
                  onKeyDown={handleTagInputKeyDown}
                  className="w-full p-2 border border-gray-300 rounded"
                />
                <button
                  type="button"
                  onClick={handleAddTagClick}
                  className="px-4 py-2 bg-indigo-600 text-white rounded mr-2 h-10 whitespace-nowrap"
                >
                  Add tags
                </button>
              </div>
            </div>
            <div className="flex justify-end">
              <button
                onClick={handleAddTag}
                className="px-4 py-2 bg-indigo-600 text-white rounded mr-2"
              >
                Save
              </button>
              <button
                onClick={() =>
                  setTagModalData({
                    visible: false,
                    tableName: "",
                    queryIndex: null,
                    customName: "",
                    tags: [],
                  })
                }
                className="px-4 py-2 bg-gray-600 text-white rounded"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {eventFilterModal.visible && (
        <div
          onClick={handleCloseModalOnClickOutside(() =>
            setEventFilterModal({ ...eventFilterModal, visible: false })
          )}
          className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white p-6 rounded-lg shadow-lg max-w-2xl w-full"
          >
            <h3 className="text-xl font-bold mb-4">Set Event Time Filter</h3>

            <div className="mb-4">
              <div className="text-sm font-semibold mb-2">Platform</div>
              <div className="flex flex-wrap gap-3">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="pastePlatform"
                    value="defender"
                    checked={pastePlatform === "defender"}
                    onChange={() => setPastePlatform("defender")}
                  />
                  Defender Advanced Hunting
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="pastePlatform"
                    value="sentinel"
                    checked={pastePlatform === "sentinel"}
                    onChange={() => setPastePlatform("sentinel")}
                  />
                  Sentinel (Log Analytics)
                </label>
              </div>
              <div className="text-xs text-gray-600 mt-2 font-mono break-all">
                {pasteEventTime}
              </div>
            </div>
            <div className="mb-2">
              <label className="block mb-1">
                Date and Time (e.g., 2025-04-10T07:48:52.97023Z):
              </label>
              <input
                type="text"
                value={eventFilterModal.eventTime}
                onChange={(e) =>
                  setEventFilterModal((prev) => ({
                    ...prev,
                    eventTime: e.target.value,
                  }))
                }
                className="w-full p-2 border border-gray-300 rounded"
              />
            </div>
            <div className="mb-2">
              <label className="block mb-1">Offset Before (minutes):</label>
              <input
                type="number"
                value={eventFilterModal.offsetBefore}
                onChange={(e) =>
                  setEventFilterModal((prev) => ({
                    ...prev,
                    offsetBefore: Number(e.target.value),
                  }))
                }
                className="w-full p-2 border border-gray-300 rounded"
              />
            </div>
            <div className="mb-4">
              <label className="block mb-1">Offset After (minutes):</label>
              <input
                type="number"
                value={eventFilterModal.offsetAfter}
                onChange={(e) =>
                  setEventFilterModal((prev) => ({
                    ...prev,
                    offsetAfter: Number(e.target.value),
                  }))
                }
                className="w-full p-2 border border-gray-300 rounded"
              />
            </div>
            <div className="flex justify-end">
              <button
                onClick={handleSaveEventFilterModal}
                className="px-4 py-2 bg-indigo-600 text-white rounded mr-2"
              >
                Save
              </button>
              <button
                onClick={handleCancelEventFilterModal}
                className="px-4 py-2 bg-gray-600 text-white rounded"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {showAddTileModal && (
        <div
          onClick={handleCloseModalOnClickOutside(() =>
            setShowAddTileModal(false)
          )}
          className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
        >
          <div
            ref={tileModalRef}
            onClick={(e) => e.stopPropagation()}
            style={{
              width: tileModalSize.width,
              height: tileModalSize.height,
              position: "relative",
            }}
            className="bg-white p-6 rounded-lg shadow-lg flex flex-col"
          >
            <h3 className="text-xl font-bold mb-4">
              {editingTile ? "Edit Quick Query" : "Add New Quick Query"}
            </h3>
            <p className="mb-4 text-sm text-gray-700">
              {editingTile
                ? "Update or remove your custom quick query."
                : 'Add a title and paste your KQL query. You can include | search "" to add data from the main dashboard.'}
            </p>
            <input
              type="text"
              placeholder="Tile Name"
              value={newTileName}
              onChange={(e) => setNewTileName(e.target.value)}
              maxLength={20}
              className="w-full p-2 border border-gray-300 rounded mb-4"
            />
            <textarea
              placeholder="Paste your KQL query here..."
              value={newTileQuery}
              onChange={(e) => setNewTileQuery(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md mb-4 flex-grow"
            />
            <div className="flex justify-end space-x-2">
              {editingTile && (
                <button
                  onClick={() => {
                    const processedQuery = newTileQuery.replace(
                      /\|\s*search\s*""/g,
                      `| search ${ensureKqlQuoted(searchValue)}`
                    );
                    setBlueTiles((prev) =>
                      prev.filter((t) => t.id !== editingTile.id)
                    );
                    setShowAddTileModal(false);
                  }}
                  className="px-4 py-2 bg-red-600 text-white rounded"
                >
                  Delete
                </button>
              )}
              <button
                onClick={() => {
                  const processedQuery = newTileQuery.replace(
                    /\|\s*search\s*""/g,
                    `| search ${ensureKqlQuoted(searchValue)}`
                  );
                  if (editingTile) {
                    setBlueTiles((prev) =>
                      prev.map((tile) =>
                        tile.id === editingTile.id
                          ? {
                              ...tile,
                              label: newTileName.trim(),
                              queryTemplate: newTileQuery,
                            }
                          : tile
                      )
                    );
                  } else {
                    if (!newTileQuery.trim()) return;
                    const newTile = {
                      id: `tile-${Date.now()}`,
                      label: newTileName.trim() || "Custom Tile",
                      queryTemplate: newTileQuery,
                    };
                    setBlueTiles((prev) => {
                      const plusIndex = prev.findIndex((t) => t.isPlus);
                      if (plusIndex === -1) return [...prev, newTile];
                      const newTiles = [...prev];
                      newTiles.splice(plusIndex, 0, newTile);
                      return newTiles;
                    });
                  }
                  setShowAddTileModal(false);
                }}
                className="px-4 py-2 bg-indigo-600 text-white rounded"
              >
                {editingTile ? "Update" : "Add"}
              </button>
              <button
                onClick={() => setShowAddTileModal(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- Drawer overlay (mobile) --- */}
      {(leftDrawerOpen || rightDrawerOpen) && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50"
          onClick={() => {
            setLeftDrawerOpen(false);
            setRightDrawerOpen(false);
          }}
        />
      )}

      <div className="qb-shell">
        <aside
          className={
            "qb-left qb-aside-left bg-gray-800 text-white p-4 overflow-y-auto" +
            (leftDrawerOpen ? " qb-open" : "")
          }
        >
          <div className="qb-mobile-bar justify-end w-full mb-2">
            <button
              onClick={() => setLeftDrawerOpen(false)}
              className="px-3 py-2 bg-gray-700 text-white rounded-md border border-gray-600"
            >
              ✕ Close
            </button>
          </div>
          <div className="flex flex-col h-full">
            <div className="flex-1">
              <div className="flex items-center justify-between gap-2 mb-1">
                <h2 className="text-lg font-bold mb-4">Favorite Tables</h2>
              </div>
              <input
                type="text"
                placeholder="Search by name and tags..."
                value={favoritesSearch}
                onChange={(e) => setFavoritesSearch(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded mb-4 text-black"
              />
              <ul>
                {tables
                  .filter((table) => favoriteQueries[table.name]?.length)
                  .filter((table) => {
                    const search = favoritesSearch.toLowerCase();
                    if (!search) return true;
                    const favs = favoriteQueries[table.name] || [];
                    const namesObj = customFavoriteNames[table.name] || {};
                    // Check custom names and tags
                    for (const idx of favs) {
                      const customName = (
                        namesObj[idx]?.customName || ""
                      ).toLowerCase();
                      if (customName.includes(search)) return true;
                      const tags = namesObj[idx]?.tags || [];
                      if (
                        tags.some((tag) => tag.toLowerCase().includes(search))
                      )
                        return true;
                    }
                    return false;
                  })
                  .map((table) => {
                    const favs = favoriteQueries[table.name] || [];
                    const activeIndex = selectedFavorite[table.name] ?? favs[0];
                    const isDropdownOpen =
                      favoriteDropdown[table.name] || false;
                    return (
                      <li
                        key={table.name}
                        className="py-1 border-b border-gray-600"
                      >
                        <div className="flex items-center justify-between relative">
                          <div className="flex items-center">
                            <span className="mr-2 text-gray-400">☰</span>
                            <button
                              onClick={() => {
                                const processedQuery = newTileQuery.replace(
                                  /\|\s*search\s*""/g,
                                  `| search ${ensureKqlQuoted(searchValue)}`
                                );
                                // scroll into view of query card
                                const el = document.getElementById(
                                  `query-${table.name}-${activeIndex}`
                                );
                                if (el)
                                  el.scrollIntoView({
                                    behavior: "smooth",
                                    block: "start",
                                  });
                              }}
                              className="hover:text-yellow-400 transition"
                            >
                              {table.name}
                            </button>
                          </div>
                          {favs.length > 0 && (
                            <div className="relative ml-2 favorite-dropdown">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setFavoriteDropdown({
                                    [table.name]: !favoriteDropdown[table.name],
                                  });
                                }}
                                className="bg-blue-600 text-white px-2 py-0.5 rounded focus:outline-none text-xs"
                              >
                                [{favs.length}]
                              </button>
                              {isDropdownOpen && (
                                <div className="absolute top-full right-2 mt-1 bg-white bg-opacity-80 border rounded shadow-lg z-10 text-black max-h-60 overflow-y-auto w-52">
                                  <div className="p-2 border-b">
                                    <input
                                      type="text"
                                      placeholder="Filter..."
                                      value={dropdownFilter}
                                      onChange={(e) =>
                                        setDropdownFilter(e.target.value)
                                      }
                                      className="w-full p-2 text-xs"
                                    />
                                  </div>
                                  {favs
                                    .filter((idx) => {
                                      const filter =
                                        dropdownFilter.toLowerCase();
                                      if (!filter) return true;
                                      const customName = (
                                        customFavoriteNames[table.name]?.[idx]
                                          ?.customName || ""
                                      ).toLowerCase();
                                      const tags =
                                        customFavoriteNames[table.name]?.[idx]
                                          ?.tags || [];
                                      return (
                                        customName.includes(filter) ||
                                        tags.some((tag) =>
                                          tag.toLowerCase().includes(filter)
                                        )
                                      );
                                    })
                                    .map((idx) => (
                                      <div
                                        key={idx}
                                        className="flex items-center justify-between cursor-pointer"
                                        onClick={() => {
                                          const processedQuery =
                                            newTileQuery.replace(
                                              /\|\s*search\s*""/g,
                                              `| search ${ensureKqlQuoted(
                                                searchValue
                                              )}`
                                            );
                                          setSelectedFavorite((prev) => ({
                                            ...prev,
                                            [table.name]: idx,
                                          }));
                                          setFavoriteDropdown({});
                                          setDropdownFilter("");
                                          setTimeout(() => {
                                            const el = document.getElementById(
                                              `query-${table.name}-${idx}`
                                            );
                                            if (el)
                                              el.scrollIntoView({
                                                behavior: "smooth",
                                                block: "start",
                                              });
                                          }, 50);
                                        }}
                                      >
                                        <div className="px-4 py-2 hover:bg-gray-200 flex-1 text-xs">
                                          {customFavoriteNames[table.name]?.[
                                            idx
                                          ]?.customName || table.comment}
                                          {(
                                            customFavoriteNames[table.name]?.[
                                              idx
                                            ]?.tags || []
                                          ).map((tag) => (
                                            <span
                                              key={tag}
                                              className="ml-1 px-2 py-0.5 rounded text-xs bg-yellow-300 text-black"
                                            >
                                              #{tag}
                                            </span>
                                          ))}
                                        </div>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setTagModalData({
                                              visible: true,
                                              tableName: table.name,
                                              queryIndex: idx,
                                              customName:
                                                customFavoriteNames[
                                                  table.name
                                                ]?.[idx]?.customName || "",
                                              tags:
                                                customFavoriteNames[
                                                  table.name
                                                ]?.[idx]?.tags || [],
                                            });
                                          }}
                                          className="px-2 py-1 text-xs text-blue-600 hover:text-blue-800"
                                          title="Edit favorite"
                                        >
                                          ✏️
                                        </button>
                                      </div>
                                    ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </li>
                    );
                  })}
              </ul>
              <h2 className="text-lg font-bold mt-6 mb-2">Table Navigation</h2>
              <input
                type="text"
                placeholder="Search table..."
                value={tableNavSearch}
                onChange={(e) => setTableNavSearch(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded mb-3 text-black"
              />
              <ul>
                {tables
                  .filter((t) => {
                    const q = tableNavSearch.trim().toLowerCase();
                    if (!q) return true;
                    return String(t.name || "")
                      .toLowerCase()
                      .includes(q);
                  })
                  .map((table, index) => (
                    <li
                      key={table.name}
                      className="py-1 border-b border-gray-600"
                      draggable
                      onDragStart={(e) =>
                        handleDragStartWrapper(e, index, tables)
                      }
                      onDragEnter={(e) => handleDragEnter(e, index)}
                      onDragLeave={handleDragLeave}
                      onDragOver={handleDragOver}
                      onDragEnd={(e) => handleDragEnd(e, tables, setTables)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <span className="mr-2 text-gray-400">☰</span>
                          <button
                            onClick={() => {
                              const processedQuery = newTileQuery.replace(
                                /\|\s*search\s*""/g,
                                `| search ${ensureKqlQuoted(searchValue)}`
                              );
                              const activeIdx =
                                selectedFavorite[table.name] ?? 0;
                              const el = document.getElementById(
                                `query-${table.name}-${activeIdx}`
                              );
                              if (el) el.scrollIntoView({ behavior: "smooth" });
                            }}
                            className="hover:text-yellow-400 transition"
                          >
                            {table.name}
                          </button>
                        </div>
                      </div>
                    </li>
                  ))}
              </ul>
            </div>
            <div className="mt-6">
              <button
                onClick={toggleSettingsPanel}
                className="w-full py-3 bg-purple-600 text-white font-semibold rounded-md"
              >
                Settings - import or export
              </button>
              <button
                onClick={() =>
                  window.open(
                    "https://github.com/kql100/issues/issues",
                    "_blank"
                  )
                }
                className="w-full py-3 bg-purple-600 text-white font-semibold rounded-md mt-4"
              >
                Report any issues or share suggestions to help improve KQLTool
                feel free to include your KQL queries too!
              </button>
              {showSettingsPanel && (
                <div
                  ref={settingsRef}
                  className="mt-4 p-4 bg-gray-700 rounded-md"
                >
                  <h3 className="text-xl font-bold mb-2">Settings</h3>
                  <textarea
                    className="w-full p-2 border border-gray-300 rounded mb-2 focus:ring-2 focus:ring-indigo-500 text-black"
                    value={settingsData}
                    onChange={handleSettingsDataChange}
                    placeholder="Paste settings JSON here"
                    rows={6}
                  />
                  <input
                    type="text"
                    placeholder="Enter settings URL (e.g., GitHub raw URL)"
                    value={settingsURL}
                    onChange={(e) => setSettingsURL(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded mb-2 focus:ring-2 focus:ring-indigo-500 text-black"
                  />
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={handleLoadFromURL}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
                    >
                      Load Settings from URL
                    </button>
                    <button
                      onClick={handleImportSettings}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                    >
                      Import settings you pasted above
                    </button>
                    <button
                      onClick={handleDownloadSettings}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                    >
                      Download Settings
                    </button>
                    <button
                      onClick={clearAllSettings}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                    >
                      Clear All Settings
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </aside>
        <main className="qb-main p-0">
          <div className="max-w-5xl mx-auto bg-gray-900 rounded-2xl border border-white/10 p-6">
            <div className="qb-mobile-bar items-center justify-between gap-2 mb-4">
              <button
                onClick={() => {
                  setRightDrawerOpen(false);
                  setLeftDrawerOpen(true);
                }}
                className="px-3 py-2 bg-gray-800 text-white rounded-md border border-gray-700"
              >
                ☰ Tables
              </button>
              <button
                onClick={() => {
                  setLeftDrawerOpen(false);
                  setRightDrawerOpen(true);
                }}
                className="px-3 py-2 bg-gray-800 text-white rounded-md border border-gray-700"
              >
                🧰 Tools
              </button>
            </div>
            <div className="w-full max-w-5xl mx-auto bg-white p-6 rounded-lg shadow-lg">
              {/* Privacy / Storage note */}
              <div className="mb-4 rounded-md border border-blue-300 bg-blue-100 p-4 text-blue-900 text-sm">
                <div className="font-semibold mb-1">Privacy / Storage note</div>
                <div>
                  This tool does not collect or send any data anywhere.
                  Everything you save (tables, favorites, queries, settings) is
                  stored only in your browser. All settings are saved locally,
                  and you can safely close your browser at any time. If you
                  clear or reset your browser data in the browser settings, all
                  saved data will be lost. Recommended: export your settings to
                  a file from the Settings panel.
                </div>
              </div>
              <button
                onClick={defineTablesFromEnv}
                className="w-full py-3 bg-indigo-600 text-white font-semibold rounded-md mb-6"
              >
                Define your tables from your environment
              </button>
              {showPopup && (
                <div
                  onClick={handleCloseModalOnClickOutside(closePopup)}
                  className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
                >
                  <div
                    onClick={(e) => e.stopPropagation()}
                    className="bg-white p-6 rounded-lg shadow-lg max-w-2xl w-full"
                  >
                    <h3 className="text-xl font-bold mb-4">
                      Define your tables from your environment
                    </h3>
                    <p className="mb-4 text-sm text-gray-700">
                      Run the following query in Sentinel (or Advanced Threat
                      Hunting in Defender) and paste the result. Ensure that
                      each table is on a new line, then click "Add Your Tables".
                    </p>

                    <div className="mb-4">
                      <div className="text-sm font-semibold mb-2">Platform</div>
                      <div className="flex flex-wrap gap-3">
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="radio"
                            name="envPlatform"
                            value="sentinel"
                            checked={envPlatform === "sentinel"}
                            onChange={() => setEnvPlatform("sentinel")}
                          />
                          Sentinel (Log Analytics)
                        </label>
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="radio"
                            name="envPlatform"
                            value="defender"
                            checked={envPlatform === "defender"}
                            onChange={() => setEnvPlatform("defender")}
                          />
                          Defender Advanced Hunting
                        </label>
                      </div>
                    </div>

                    <div className="relative bg-gray-900 text-white p-4 mb-4 rounded-md">
                      <pre className="whitespace-pre text-sm font-mono overflow-x-auto">
                        {defineTablesKql}
                      </pre>
                      <button
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(
                              defineTablesKql
                            );
                            setShowSavedCopyToast(true);
                            setTimeout(
                              () => setShowSavedCopyToast(false),
                              1200
                            );
                          } catch {
                            // ignore
                          }
                        }}
                        className="absolute top-2 right-2 bg-gray-700 text-white px-2 py-0.5 rounded text-xs hover:bg-gray-600"
                      >
                        Copy
                      </button>
                    </div>
                    <textarea
                      className="w-full p-3 border border-gray-300 rounded-md mt-4 h-48"
                      placeholder="Paste result here"
                      value={copiedResult}
                      onChange={(e) => setCopiedResult(e.target.value)}
                    />
                    <div className="flex justify-end mt-4">
                      <button
                        onClick={handleAddTables}
                        className="px-4 py-2 bg-green-600 text-white rounded-md mr-2"
                      >
                        Add Your Tables
                      </button>
                      <button
                        onClick={closePopup}
                        className="px-4 py-2 bg-gray-600 text-white rounded-md"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {showSavedCopyToast && (
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-gray-900 text-white px-4 py-2 rounded shadow-lg z-50 text-sm">
                  Copied to clipboard ✅
                </div>
              )}
              <div className="flex justify-end mb-2">
                <button
                  onClick={() => setShowGuide((prev) => !prev)}
                  className="px-3 py-1 bg-gray-700 text-white rounded-md text-sm"
                >
                  {showGuide ? "Hide Guide" : "Show Guide"}
                </button>
              </div>
              {showGuide && (
                <div className="mb-6 p-4 bg-blue-100 border border-blue-300 rounded">
                  <p className="text-sm">
                    Recommendations: Easily manage your most frequent queries or
                    notes with quick tiles. Click once to copy the data – ready
                    to paste wherever you need it.
                  </p>
                </div>
              )}
              <div className="flex items-center gap-3 mb-3">
                <span className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                  Paste data to search:
                </span>
                <div className="flex-1 h-px bg-gray-300" />
              </div>
              <input
                type="text"
                placeholder="Paste your data..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-md mb-4"
              />
              <div className="flex justify-end mb-2">
                <button
                  onClick={() => setShowGuide((prev) => !prev)}
                  className="px-3 py-1 bg-gray-700 text-white rounded-md text-sm"
                >
                  {showGuide ? "Hide Guide" : "Show Guide"}
                </button>
              </div>
              {showGuide && (
                <div className="mb-6 p-4 bg-blue-100 border border-blue-300 rounded">
                  <p className="text-sm">
                    Recommendations: Easily manage your most frequent queries or
                    notes with quick tiles. Click once to copy the data – ready
                    to paste wherever you need it.
                  </p>
                </div>
              )}
              {/* Highlighted tile: Search Around Event */}
              <button
                onClick={() => setShowAroundPopup(true)}
                className="w-full py-4 px-4 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-md shadow transition mb-3 text-lg"
                title="Search Around Event"
              >
                Search Around Event
              </button>

              <div className="mb-4 flex flex-wrap gap-2">
                {renderSmartTiles()}
              </div>

              {/* Saved Queries (tiles) */}
              <div className="mt-6">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                    Saved Queries or notes - easy to copy or search
                  </span>
                  <div className="flex-1 h-px bg-gray-300" />

                  <button
                    type="button"
                    onClick={() => openSaveQueryModal("new")}
                    className="w-9 h-9 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white flex items-center justify-center text-2xl leading-none"
                    title="Add new saved query / note"
                    aria-label="Add"
                  >
                    +
                  </button>

                  <button
                    type="button"
                    onClick={() => setShowSavedQueriesSearch((p) => !p)}
                    className="w-9 h-9 rounded-md border border-gray-400 hover:bg-gray-100 flex items-center justify-center"
                    title="Search saved queries / notes"
                    aria-label="Search"
                  >
                    🔍
                  </button>
                </div>

                {showSavedQueriesSearch ? (
                  <input
                    type="text"
                    placeholder="Search saved queries / notes (title, description, tags)..."
                    value={savedQueriesSearch}
                    onChange={(e) => setSavedQueriesSearch(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded mb-3"
                  />
                ) : null}

                <div className="flex flex-wrap gap-2">
                  {savedQueries
                    .filter((q) => {
                      const s = savedQueriesSearch.trim().toLowerCase();
                      if (!s) return true;
                      const title = String(q.title || "").toLowerCase();
                      const desc = String(q.description || "").toLowerCase();
                      const tags = (q.tags || []).join(" ").toLowerCase();
                      return (
                        title.includes(s) ||
                        desc.includes(s) ||
                        tags.includes(s)
                      );
                    })
                    .map((q) => (
                      <div
                        key={q.id}
                        className="relative inline-block w-44"
                        onDragOver={handleSavedQueryDragOver}
                        onDragEnter={handleSavedQueryDragEnter}
                        onDragLeave={handleSavedQueryDragLeave}
                        onDrop={(e) => handleSavedQueryDrop(e, q.id)}
                      >
                        <button
                          draggable
                          onDragStart={(e) =>
                            handleSavedQueryDragStart(e, q.id)
                          }
                          onDragEnd={handleSavedQueryDragEnd}
                          onClick={() => handleSavedQueryTileClick(q)}
                          onDoubleClick={() =>
                            handleSavedQueryTileDoubleClick(q)
                          }
                          className="w-full py-2 px-3 pr-10 bg-blue-400 hover:bg-blue-500 text-white text-sm font-medium rounded-lg shadow transition text-left"
                          title="Click to copy • Double-click to edit"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="truncate">{q.title}</span>
                            {q.pinned ? <span title="Pinned">⭐</span> : null}
                          </div>
                          {/* tags hidden on tile (still searchable) */}
                        </button>

                        {/* Delete (does not trigger copy/edit) */}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            deleteSavedQuery(q.id);
                          }}
                          className="absolute top-1 right-1 w-7 h-7 rounded-md bg-white/20 hover:bg-white/30 text-white flex items-center justify-center"
                          title="Delete"
                          aria-label="Delete"
                        >
                          🗑️
                        </button>
                      </div>
                    ))}
                </div>
              </div>

              <div className="mt-6 flex items-center gap-3 mb-3">
                <span className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                  Select available tables:
                </span>
                <div className="flex-1 h-px bg-gray-300" />
              </div>
              <div className="relative bg-white p-4 rounded-md shadow overflow-hidden w-full">
                <div
                  ref={tablesContainerRef}
                  className="grid gap-2 overflow-x-auto px-4 hide-scrollbar"
                  style={{
                    gridAutoFlow: "column",
                    gridTemplateRows: "repeat(6, auto)",
                  }}
                >
                  {tables.map((table) => (
                    <div
                      key={table.name}
                      className={`flex items-center justify-between space-x-1 p-1 border border-gray-300 rounded ${
                        selectedTables.includes(table.name)
                          ? "bg-indigo-100"
                          : ""
                      }`}
                    >
                      <div className="flex items-center space-x-1">
                        <input
                          type="checkbox"
                          checked={selectedTables.includes(table.name)}
                          onChange={() =>
                            setSelectedTables((prev) =>
                              prev.includes(table.name)
                                ? prev.filter((t) => t !== table.name)
                                : [...prev, table.name]
                            )
                          }
                          className="h-4 w-4 border-gray-300 rounded"
                        />
                        <span className="text-xs whitespace-nowrap overflow-hidden">
                          {table.name}
                        </span>
                      </div>
                      <button
                        onClick={() => removeTable(table.name)}
                        className="text-xs text-red-500 hover:text-red-700"
                        title="Remove table"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => {
                    const processedQuery = newTileQuery.replace(
                      /\|\s*search\s*""/g,
                      `| search ${ensureKqlQuoted(searchValue)}`
                    );
                    tablesContainerRef.current &&
                      tablesContainerRef.current.scrollBy({
                        left: -200,
                        behavior: "smooth",
                      });
                  }}
                  className="absolute left-2 top-1/2 transform -translate-y-1/2 z-10 bg-gray-200 rounded-full p-2 hover:bg-gray-300"
                >
                  {"<"}
                </button>
                <button
                  onClick={() => {
                    const processedQuery = newTileQuery.replace(
                      /\|\s*search\s*""/g,
                      `| search ${ensureKqlQuoted(searchValue)}`
                    );
                    tablesContainerRef.current &&
                      tablesContainerRef.current.scrollBy({
                        left: 200,
                        behavior: "smooth",
                      });
                  }}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 z-10 bg-gray-200 rounded-full p-2 hover:bg-gray-300"
                >
                  {">"}
                </button>
              </div>
              <h2 className="text-xl font-bold mt-6 mb-4">Add a new table:</h2>
              <input
                type="text"
                placeholder="Table name"
                value={newTableName}
                onChange={(e) => setNewTableName(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-md mb-2"
              />
              <textarea
                id="add-new-table-query"
                placeholder={`📌 Paste the full KQL query. If you add the statement: 
  | search ${ensureKqlQuoted("")} 
  to your query, the data you entered above will be included.
  For example:
  CustomEmailGateWay
  | search ${ensureKqlQuoted("customfilename")}
  | where Timestamp >= ago(30d)
  | project TimeGenerated, Subject, SenderEmailAddress, RecipientEmailAddress`}
                value={newTableQuery}
                onChange={(e) => setNewTableQuery(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-md mb-4 h-48"
              />
              <button
                onClick={addNewTable}
                className="w-full py-3 bg-indigo-600 text-white font-semibold rounded-md"
              >
                Add Table
              </button>
              <div className="flex justify-end mt-4">
                <button
                  onClick={() => setShowInstructions((prev) => !prev)}
                  className="px-3 py-1 bg-gray-700 text-white rounded-md text-sm"
                >
                  {showInstructions ? "Hide Guide" : "Show Guide"}
                </button>
              </div>
              {showInstructions && (
                <div className="mb-6 mt-2 p-4 bg-blue-100 border border-blue-300 rounded">
                  <h2 className="text-xl font-bold mb-2">
                    How to Use Generated Query feature
                  </h2>
                  <ul className="list-disc ml-6 text-sm">
                    <li>
                      <span role="img" aria-label="clock">
                        ⏱
                      </span>{" "}
                      Click the clock icon to adjust the time (e.g., data from
                      the last 24 hours).
                    </li>
                    <li>
                      <span role="img" aria-label="watch">
                        ⏰
                      </span>{" "}
                      Use the second watch icon to set specific time ranges.
                    </li>
                    <li>
                      <span role="img" aria-label="loupe">
                        🔍
                      </span>{" "}
                      Click the loupe to see the full query, highlighted for
                      easy copying.
                    </li>
                    <li>
                      <span role="img" aria-label="heart">
                        ❤️
                      </span>{" "}
                      Click the heart icon to add the query to your favorites.
                    </li>
                    <li>
                      <span role="img" aria-label="copy">
                        📋
                      </span>{" "}
                      The copy button copies the entire query.
                    </li>
                  </ul>
                </div>
              )}
              <h2 className="text-xl font-bold mt-6 mb-4">Generated Query:</h2>
              <div className="space-y-4">{generateQuery()}</div>
            </div>
          </div>
        </main>
        {/* --- Right Sidebar Tools --- */}
        <aside
          className={
            "qb-right qb-aside-right bg-gray-800 text-white p-4 flex flex-col items-center gap-4" +
            (rightDrawerOpen ? " qb-open" : "")
          }
        >
          <div className="qb-mobile-bar justify-end w-full">
            <button
              onClick={() => setRightDrawerOpen(false)}
              className="px-3 py-2 bg-gray-700 text-white rounded-md border border-gray-600"
            >
              ✕ Close
            </button>
          </div>
          <button
            onClick={() => setShowAddCharsModal(true)}
            className="w-full py-3 bg-indigo-600 rounded-md text-white"
          >
            Add Characters to Each Line
          </button>
          <button
            onClick={() => setShowDecodeUrlModal(true)}
            className="w-full py-3 bg-indigo-600 rounded-md text-white"
          >
            Decode URL
          </button>
          <button
            onClick={() => setShowDecodeBase64Modal(true)}
            className="w-full py-3 bg-indigo-600 rounded-md text-white"
          >
            Decode Base64
          </button>
          <button
            onClick={() => setShowFindReplaceModal(true)}
            className="w-full py-3 bg-indigo-600 rounded-md text-white"
          >
            Find and Replace
          </button>
          <button
            onClick={() => setShowEmailHeaderModal(true)}
            className="w-full py-3 bg-indigo-600 rounded-md text-white"
          >
            Extract IPs from Email Header
          </button>

          <button
            onClick={() => setShowPasteEventTimeModal(true)}
            className="w-full py-3 bg-indigo-600 rounded-md text-white"
          >
            Set Event Time Filter (paste KQL)
          </button>

          <button
            onClick={() => setShowNonClickableUrlModal(true)}
            className="w-full py-3 bg-indigo-600 rounded-md text-white"
          >
            Convert to non-clickable URL
          </button>

          {/* --- VirusTotal IP Check --- */}
          <div className="mt-4 w-full flex items-center gap-2">
            <input
              type="text"
              value={ipToCheck}
              onChange={(e) => setIpToCheck(e.target.value)}
              placeholder="Enter IP"
              className="flex-1 bg-white text-black p-2 border rounded-l"
            />
            <button
              onClick={() => {
                const processedQuery = newTileQuery.replace(
                  /\|\s*search\s*""/g,
                  `| search ${ensureKqlQuoted(searchValue)}`
                );

                let q = ipToCheck.trim();
                // If user provided the query wrapped in quotes (e.g. | search "abc"), preserve them.
                const hasOuterQuotes = q.startsWith('"') && q.endsWith('"');
                if (hasOuterQuotes) {
                  q = q.slice(1, -1);
                }
                // Detect IPv4, IPv6, and hashes.
                const isIpv4 = /^[0-9]+(\.[0-9]+){3}$/.test(q);
                const isIpv6 = q.includes(":") && !q.includes(".");
                const isHash = /^[A-Fa-f0-9]{32,64}$/.test(q);
                // Strip protocol if present for domains
                if (!isIpv4 && !isIpv6 && !isHash) {
                  if (q.startsWith("https://")) q = q.slice(8);
                  else if (q.startsWith("http://")) q = q.slice(7);
                  if (q.includes(".") && !q.startsWith("www.")) {
                    q = "www." + q;
                  }
                }
                if (hasOuterQuotes) {
                  q = '"' + q + '"';
                }
                window.open(
                  `https://www.virustotal.com/gui/search/${q}`,
                  "_blank"
                );
              }}
              className="py-2 px-4 bg-indigo-600 text-white rounded-r"
            >
              Open
            </button>
          </div>

          {/* --- Save current KQL as a personal saved query --- */}
          <div className="w-full flex justify-start">
            <button
              onClick={() => openSaveQueryModal("new")}
              className="mt-4 w-14 h-14 bg-indigo-600 hover:bg-indigo-700 rounded-md flex items-center justify-center text-3xl leading-none"
              title="Save query (title/description/tags)"
            >
              +
            </button>
          </div>
        </aside>
      </div>

      {showQueryModal && (
        <div
          onClick={() => setShowQueryModal(false)}
          className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white p-6 rounded-lg shadow-lg max-w-4xl w-full max-h-[90vh] flex flex-col"
          >
            <h3 className="text-xl font-bold mb-4">Query</h3>
            <div className="flex-grow overflow-auto">
              <TextareaAutosize
                className="w-full bg-white text-sm font-mono border p-4 rounded-md resize-none outline-none"
                style={{
                  whiteSpace: "pre",
                  overflowX: "auto",
                  overflowWrap: "normal",
                }}
                value={modalQuery}
                readOnly
                ref={modalTextRef}
              />
            </div>
            <div className="flex justify-end mt-4">
              <button
                onClick={() => {
                  const processedQuery = newTileQuery.replace(
                    /\|\s*search\s*""/g,
                    `| search ${ensureKqlQuoted(searchValue)}`
                  );
                  navigator.clipboard.writeText(modalQuery);
                  setShowQueryModal(false);
                }}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md mr-2"
              >
                Copy Query
              </button>
              <button
                onClick={() => setShowQueryModal(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-md"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {modalOpen && (
        <div
          onClick={handleCloseModalOnClickOutside(closeModal)}
          className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white p-6 rounded-lg shadow-lg max-w-4xl w-full max-h-[90vh] flex flex-col"
          >
            <h3 className="text-xl font-bold mb-4">Edit Full Query</h3>
            <div className="flex-grow overflow-auto">
              <TextareaAutosize
                className="w-full bg-white text-sm font-mono border p-4 rounded-md resize-none outline-none"
                style={{
                  whiteSpace: "pre",
                  overflowX: "auto",
                  wordBreak: "normal",
                  overflowWrap: "normal",
                }}
                value={modalQueryText}
                onChange={(e) => setModalQueryText(e.target.value)}
                ref={modalTextRef}
              />
            </div>
            <div className="flex justify-end mt-4">
              <button
                onClick={() => {
                  const processedQuery = modalQueryText.replace(
                    /\|\s*search\s*""/g,
                    `| search ${ensureKqlQuoted(searchValue)}`
                  );
                  navigator.clipboard.writeText(processedQuery);
                  closeModal();
                }}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md mr-2"
              >
                Copy Query
              </button>
              <button
                onClick={closeModal}
                className="px-4 py-2 bg-gray-600 text-white rounded-md"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
